export function zeros(n) {
  let vector = [];
  for (let i = 0; i < n; i++) {
    vector.push(0);
  }
  return vector;
}

/**
 * Summarize an array of data. Returns `{min, max, total, length}`.
 *
 * @param {string or function} getter The string key of one of the feature's
 *  properties, or a function mapping each feature to the desired data.
 */
export function summarize(data) {
  return {
    min: Math.min(...data),
    max: Math.max(...data),
    total: sum(data),
    length: data.length
  };
}

// From https://stackoverflow.com/questions/2901102/
// how-to-print-a-number-with-commas-as-thousands-separators-in-javascript#2901298
export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function roundToDecimal(n, places) {
  return Math.round(n * Math.pow(10, places)) / Math.pow(10, places);
}

export function sum(values) {
  return values.reduce((total, value) => total + value, 0);
}

export function divideOrZeroIfNaN(x, y) {
  return ["case", [">", y, 0], ["/", x, y], 0];
}

export function extent(values) {
  return Math.min(...values) - Math.max(...values);
}

export function asPercent(value, total) {
  return `${Math.round(100 * (value / total))}%`;
}

export function replace(list, i, item) {
  return [...list.slice(0, i), item, ...list.slice(i + 1)];
}

// Light-weight redux implementation

export function createReducer(handlers) {
  return (state, action) => {
    if (handlers.hasOwnProperty(action.type)) {
      return handlers[action.type](state, action);
    }
    return state;
  };
}

export function combineReducers(reducers) {
  return (state, action) => {
    let hasChanged = false;
    let nextState = {};

    for (let key in reducers) {
      nextState[key] = reducers[key](state[key], action);
      hasChanged = hasChanged || nextState[key] !== state[key];
    }

    return hasChanged ? nextState : state;
  };
}

export function createActions(handlers) {
  let actions = {};
  for (let actionType in handlers) {
    actions[actionType] = actionInfo => ({
      ...actionInfo,
      type: actionType
    });
  }
  return actions;
}

export function bindDispatchToActions(actions, dispatch) {
  let boundActions = {};
  for (let actionType in actions) {
    boundActions[actionType] = actionInfo =>
      dispatch(boundActions[actionType](actionInfo));
  }
  return boundActions;
}

/**
 * Handle HTTP responses by providing handlers for HTTP status codes.
 *
 * The `handlers` object should have handlers for each status code you want
 * to handle (e.g. 200, 500) as well as a "default" handler for all other
 * cases.
 *
 * @param {object} handlers
 */
export function handleResponse(handlers) {
  handlers = {
    // eslint-disable-next-line no-console
    default: resp => console.error("Request failed", resp),
    ...handlers
  };
  return response => {
    if (handlers.hasOwnProperty(response.status)) {
      return handlers[response.status](response);
    } else {
      return handlers.default(response);
    }
  };
}

export function isString(x) {
  return typeof x === "string" || x instanceof String;
}

// Copied from stackoverflow https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript
export function dec2hex(dec) {
  return ("0" + dec.toString(16)).substr(-2);
}

export function generateId(len) {
  const arr = new Uint8Array((len || 40) / 2);
  const crypto = window.crypto ? window.crypto : window.msCrypto;
  crypto.getRandomValues(arr);
  return Array.from(arr, dec2hex).join("");
}

export function download(filename, text, isbinary) {
  let blob;
  if (isbinary) {
    blob = new Blob([text], {
      type: 'application/octet-stream'
    });
  }
  let element = document.createElement("a");
  element.setAttribute(
    "href",
    isbinary
      ? window.URL.createObjectURL(blob)
      : "data:text/plain;charset=utf-8," + encodeURIComponent(text)
  );
  element.setAttribute("download", filename);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export function bindAll(keys, obj) {
  keys.forEach(key => {
    obj[key] = obj[key].bind(obj);
  });
}

export function boundsOfGJ(gj) {
  var coords, bbox;
  if (!gj.hasOwnProperty('type')) return;
  coords = getCoordinatesDump(gj);
  bbox = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY,
  Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY,];
  return coords.reduce(function (prev, coord) {
    return [
      Math.min(coord[0], prev[0]),
      Math.min(coord[1], prev[1]),
      Math.max(coord[0], prev[2]),
      Math.max(coord[1], prev[3])
    ];
  }, bbox);
};

function getCoordinatesDump(gj) {
  var coords;
  if (gj.type == 'Point') {
    coords = [gj.coordinates];
  } else if (gj.type == 'LineString' || gj.type == 'MultiPoint') {
    coords = gj.coordinates;
  } else if (gj.type == 'Polygon' || gj.type == 'MultiLineString') {
    coords = gj.coordinates.reduce(function (dump, part) {
      return dump.concat(part);
    }, []);
  } else if (gj.type == 'MultiPolygon') {
    coords = gj.coordinates.reduce(function (dump, poly) {
      return dump.concat(poly.reduce(function (points, part) {
        return points.concat(part);
      }, []));
    }, []);
  } else if (gj.type == 'Feature') {
    coords = getCoordinatesDump(gj.geometry);
  } else if (gj.type == 'GeometryCollection') {
    coords = gj.geometries.reduce(function (dump, g) {
      return dump.concat(getCoordinatesDump(g));
    }, []);
  } else if (gj.type == 'FeatureCollection') {
    coords = gj.features.reduce(function (dump, f) {
      return dump.concat(getCoordinatesDump(f));
    }, []);
  }
  return coords;
}

export const COUNTIES_TILESET = {
  sourceLayer: "cb_2018_us_county_500k-6p4p3f",
  source: { type: "vector", url: "mapbox://districtr.6fcd9f0h" }
};

export const stateNameToFips = {
  alabama: "01",
  alaska: "02",
  arizona: "04",
  arkansas: "05",
  california: "06",
  colorado: "08",
  connecticut: "09",
  delaware: 10,
  "district of columbia": 11,
  district_of_columbia: 11,
  districtofcolumbia: 11,
  florida: 12,
  georgia: 13,
  hawaii: 15,
  idaho: 16,
  illinois: 17,
  indiana: 18,
  iowa: 19,
  kansas: 20,
  kentucky: 21,
  louisiana: 22,
  maine: 23,
  maryland: 24,
  massachusetts: 25,
  ma: 25,
  michigan: 26,
  minnesota: 27,
  mississippi: 28,
  missouri: 29,
  montana: 30,
  nebraska: 31,
  nevada: 32,
  "new hampshire": 33,
  new_hampshire: 33,
  newhampshire: 33,
  "new jersey": 34,
  new_jersey: 34,
  newjersey: 34,
  "new mexico": 35,
  new_mexico: 35,
  newmexico: 35,
  "new york": 36,
  new_york: 36,
  newyork: 36,
  "north carolina": 37,
  north_carolina: 37,
  northcarolina: 37,
  nc: 37,
  "north dakota": 38,
  north_dakota: 38,
  northdakota: 38,
  ohio: 39,
  oklahoma: 40,
  oregon: 41,
  pennsylvania: 42,
  "rhode island": 44,
  rhode_island: 44,
  rhodeisland: 44,
  "south carolina": 45,
  southcarolina: 45,
  south_carolina: 45,
  "south dakota": 46,
  south_dakota: 46,
  southdakota: 46,
  tennessee: 47,
  texas: 48,
  utah: 49,
  vermont: 50,
  virginia: 51,
  washington: 53,
  "west virginia": 54,
  westvirginia: 55,
  west_virginia: 54,
  wisconsin: 55,
  wyoming: 56,
  "puerto rico": 72,
  puertorico: 72,
  puerto_rico: 72
};

export function spatial_abilities(id) {
  const status = {
    alabama: {
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    alaska: {
      number_markers: true,
      native_american: true,
      shapefile: true,
      school_districts: true,
      municipalities: true,
      current_districts: true,
      portal: {
        endpoint: 'https://www.akredistrict.org/map-comment',
      },
    },
    alaska_blocks: {
      coalition: false,
      school_districts: true,
      municipalities: true,
      current_districts: true,
      portal: {
        endpoint: 'https://www.akredistrict.org/map-comment',
        saveredirect: 'www.akredistrict.org/create/edit.html',
      },
    },
    arizona: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      // find_unpainted: true,
    },
    maricopa: {
      native_american: true,
      number_markers: true,
    },
    nwaz: {
      native_american: true,
      number_markers: true,
    },
    seaz: {
      native_american: true,
      number_markers: true,
    },
    mesaaz: {
      native_american: true,
      number_markers: true,
      border: true,
      sideload: true,
    },
    phoenix: {
      native_american: true,
      number_markers: true,
      border: true,
    },
    yuma: {
      native_american: true,
      number_markers: true,
    },
    arkansas: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    california: {
      number_markers: true,
      native_american: true,
      county_brush: true,
      shapefile: true,
      sideload: true,
      // find_unpainted: true,
    },
    ca_contracosta: {
      number_markers: false,
      border: true,
      shapefile: true,
      coalition: false,
      portal: {
        endpoint: 'https://portal.contracosta-mapping.org'
      }
    },
    contracosta: {
      number_markers: true,
      border: true,
      shapefile: true,
      municipalities: true,
      portal: {
        endpoint: 'https://portal.contracosta-mapping.org'
      }
    },
    pasorobles: {
      number_markers: true,
      border: true,
      shapefile: true,
    },
    sacramento: {
      coalition: false,
      shapefile: true,
      number_markers: true,
      border: true,
      sideload: true,
      // divisor: 1000,
    },
    ca_sonoma: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_pasadena: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_goleta: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_santabarbara: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_fresno: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_fresno_ci: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_nevada: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_merced: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_kings: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    lake_el: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_chino: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_campbell: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_fremont: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_buellton: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_vallejo: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_grover: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_oceano: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_sm_county: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_sanbenito: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
    ca_marin: {
      coalition: false,
      border: true,
      shapefile: true,
      number_markers: true,
    },
  ca_marina: {
    coalition: false,
    border: true,
    shapefile: true,
    number_markers: true,
  },
  ca_arroyo: {
    coalition: false,
    border: true,
    shapefile: true,
    number_markers: true,
  },
  ca_cvista: {
    coalition: false,
    border: true,
    shapefile: true,
    number_markers: true,
  },
  ca_camarillo: {
    coalition: false,
    border: true,
    shapefile: true,
    number_markers: true,
  },
  ca_bellflower: {
    coalition: false,
    border: true,
    shapefile: true,
    number_markers: true,
  },
  napa2021: {
    border: true,
    shapefile: true,
    number_markers: true,
  },
  napacounty2021: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
  sanjoseca: {
    border: true,
    shapefile: true,
    number_markers: true,
  },
  redwood: {
    border: true,
    shapefile: true,
    number_markers: true,
  },
  ca_ventura: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
  ca_yolo: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
  ca_solano: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
  ca_sc_county: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
  ca_siskiyou: {
    border: true,
    shapefile: true,
    number_markers: true,
    municipalities: true,
  },
    sanluiso: {
      coalition: false,
      number_markers: true,
      shapefile: true,
      border: true,
      municipalities: true,
      // sideload: true,
    },
    ccsanitation: {
      // multiyear: 2018,
      shapefile: true,
    },
    ccsanitation2: {
      multiyear: 2018,
      shapefile: true,
      border: true,
    },
    santa_clara: {
      border: true,
    },
    napa: {
      number_markers: true,
      border: true,
    },
    napaschools: {
      number_markers: true,
      coalition: false,
      border: true,
    },
    chicago: {
      number_markers: false,
      multiyear: 2019,
      border: true,
      parties: [
        "Rahm Emanuel",
        "Jesus \u201cChuy\u201d Garc\u00eda",
        "Lori Lightfoot",
        "Toni Preckwinkle",
      ],
      school_districts: true,
      municipalities: true,
      current_districts: true
    },
    colorado: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
      load_coi: false,
    },
    connecticut: {
      county_brush: true,
      native_american: true,
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
      load_coi: false,
    },
    dc: {
      multiyear: 2018,
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    delaware: {
      number_markers: true,
      native_american: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    florida: {
      native_american: true,
      number_markers: true,
      multiyear: 2018,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
      sideload: true,
      portal: {
        endpoint: "https://portal.florida-mapping.org",
      },
    },
    miamifl: {
      number_markers: true,
      neighborhoods: true,
      border: true,
    },
    miamidade: {
      multiyear: 2018,
      number_markers: true,
      neighborhoods: true,
    },
    fl_hills: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
    },
    fl_orange: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
    },
    fl_osceola: {
      number_markers: true,
      shapefile: true,
    },
    orlando: {
      number_markers: true,
      shapefile: true,
      border: true,
    },
    tampa: {
      number_markers: true,
      shapefile: true,
      border: true,
    },
    kissimmee: {
      number_markers: true,
      shapefile: true,
    },
    georgia: {
      number_markers: true,
      county_brush: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    hawaii: {
      number_markers: true,
      native_american: true,
      county_brush: true,
      shapefile: true,
    },
    idaho: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      contiguity: 2,
      find_unpainted: true,
    },
    illinois: {
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    indiana: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
      load_coi: false,
      school_districts: true,
    },
    iowa: {
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      // find_unpainted: true,
    },
    kansas: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    kentucky: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    lax: {
      neighborhoods: true,
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    little_rock: {
      number_markers: true,
    },
    louisiana: {
      native_american: true,
      county_brush: true, // lakes
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
      load_coi: false,
    },
    la_vra: {
      native_american: true,
      vra_effectiveness: true,
      county_brush: true, // lakes
      number_markers: false, // need different ids that louisiana precincts need to update server
    },
    batonrouge: {
      number_markers: true,
      shapefile: true,
      border: true,
      // find_unpainted: true, COI only
    },
    maine: {
      native_american: true,
      number_markers: true,
      shapefile: true,
      find_unpainted: true,
    },
    maryland: {
      number_markers: true,
      county_brush: true,
      absentee: true,
      shapefile: true,
      find_unpainted: true,
      load_coi: false,
    },
    baltimore: {
      border: true,
      number_markers: true,
      shapefile: true,
      contiguity: 2,
    },
    ma: {
      number_markers: true,
      shapefile: true,
      // find_unpainted: true,
      portal: {
        endpoint: 'https://www.massachusetts-mapping.org',
      },
    },
    ma_vra: {
      number_markers: true,
      vra_effectiveness: true,
      // shapefile: true,
      // find_unpainted: true,
    },
    lowell: {
      neighborhoods: true,
      contiguity: 2,
      number_markers: true,
      shapefile: true,
      coalition: false,
      border: true,
      // find_unpainted: true,
    },
    massachusetts: {
      portal: {
        endpoint: 'https://www.massachusetts-mapping.org',
      },
    },
    michigan: {
      number_markers: true,
      native_american: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
      current_districts: true,
      school_districts: true,
      municipalities: true,
      contiguity: 2,
      portal: {
        endpoint: 'https://www.michigan-mapping.org',
      },
    },
    minnesota: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
    },
    mn2020acs: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
    },
    olmsted: {
      number_markers: true,
      border: true,
    },
    rochestermn: {
      number_markers: true,
      border: true,
    },
    washington_mn: {
      border: true,
      number_markers: true,
      shapefile: true,
    },
    stlouis_mn: {
      border: true,
      number_markers: true,
      shapefile: true,
    },
    mississippi: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    missouri: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      load_coi: false,
      // find_unpainted: true,
      school_districts: true,
      contiguity: 2,
      portal: {
        endpoint: "https://portal.missouri-mapping.org",
      },
    },
    montana: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      shapefile: true,
      find_unpainted: true,
      county_brush: true,
    },
    nebraska: {
      // multiyear: 2018,
      // number_markers: true,
      native_american: true,
      county_brush: true,
      absentee: true,
      shapefile: true,
      load_coi: false,
      // find_unpainted: true,
    },
    nevada: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    newhampshire: {
      number_markers: true,
      shapefile: true,
      multiyear: 2018,
      // find_unpainted: true,
      school_districts: true
    },
    newjersey: {
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    new_mexico: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      contiguity: 2,
      current_districts: true,
      shapefile: true,
      find_unpainted: true
    },
    new_mexico_portal: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      contiguity: 2,
      current_districts: true,
      shapefile: true,
      find_unpainted: true,
      election_history: false,
      portal: {
        endpoint: 'https://portal.newmexico-mapping.org',
      },
    },
    new_mexico_bg: {
      native_american: true,
      shapefile: true,
      current_districts: true,
      county_brush: true,
      // find_unpainted: true,
      portal: {
        endpoint: 'https://portal.newmexico-mapping.org',
      },
    },
    santafe: {
      number_markers: true,
      contiguity: 2,
      shapefile: true,
      // find_unpainted: true,
    },
    newyork: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      // find_unpainted: true,
    },
    northcarolina: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      coi2: true,
      current_districts: true,
      shapefile: true,
      find_unpainted: true,
      coalition: false,
    },
    forsyth_nc: {
      contiguity: 2,
      shapefile: true,
    },
    buncombe: {
      contiguity: 2,
      shapefile: true,
      number_markers: true,
      border: true,
    },
    northdakota: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
      nd_benson: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38005"],
            ["<", ["get", "GEOID20"], "38006"]
          ],
      },
      nd_dunn: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38025"],
            ["<", ["get", "GEOID20"], "38026"]
          ],
      },
      nd_mckenzie: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38053"],
            ["<", ["get", "GEOID20"], "38054"]
          ],
      },
      nd_mountrail: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38061"],
            ["<", ["get", "GEOID20"], "38062"]
          ],
      },
      nd_ramsey: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38071"],
            ["<", ["get", "GEOID20"], "38072"]
          ],
      },
      nd_rollette: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38079"],
            ["<", ["get", "GEOID20"], "38080"]
          ],
      },
      nd_sioux: {
        native_american: true,
        number_markers: true,
        shapefile: true,
        county_filter: ["all",
            [">", ["get", "GEOID20"], "38085"],
            ["<", ["get", "GEOID20"], "38086"]
          ],
      },
    ohio: {
      number_markers: true,
      county_brush: true,
      shapefile: true,
      // find_unpainted: true - needs contiguity
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
      school_districts: true,
      current_districts: true
    },
    ohcentral: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    ohakron: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    ohcin: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    ohcle: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    ohse: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    ohtoledo: {
      multiyear: 2019,
      number_markers: true,
      shapefile: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    akroncanton: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    cincinnati: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    clevelandeuclid: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    columbus: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    dayton: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    limaoh: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    mansfield: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    portsmouthoh: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    toledo: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    youngstown: {
      number_markers: true,
      shapefile: true,
      border: true,
      portal: {
        endpoint: 'https://portal.ohio-mapping.org',
      },
    },
    oklahoma: {
      number_markers: true,
      native_american: true,
      county_brush: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    ontarioca: {
      number_markers: true,
      border: true,
    },
    oregon: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
    },
    portlandor: {
      number_markers: true,
      contiguity: 2,
      border: true,
    },
    pennsylvania: {
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
      sideload: true,
      load_coi: false,
      contiguity: 2,
      portal: {
        endpoint: "https://portal.pennsylvania-mapping.org",
      },
    },
    philadelphia: {
      number_markers: true,
      contiguity: 2,
      find_unpainted: true,
      border: true,
    },
    puertorico: {
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    puertorico_prec: {
      number_markers: true,
      parties: ["Nuevo Progresista", "Popular Democrático", "Nuevo Progresista", "Popular Democrático"],
      shapefile: true,
      // find_unpainted: true,
    },
    rhode_island: {
      number_markers: true,
      shapefile: true,
    },
    providence_ri: {
      border: true,
    },
    southcarolina: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
    },
    southdakota: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
      county_brush: true,
      shapefile: true,
    },
    tennessee: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
    },
    texas: {
      number_markers: true,
      county_brush: true,
      contiguity: 2,
      shapefile: true,
      find_unpainted: true,
      sideload: true,
      portal: {
        endpoint: "https://portal.texas-mapping.org",
      },
    },
    tx_vra: {
      vra_effectiveness: true,
      county_brush: true,
      number_markers: true,
    },
    austin: {
      border: true,
    },
    fortworth: {
      border: true,
    },
    houston: {
      border: true,
      number_markers: true,
      shapefile: true,
      multiyear: 2019,
    },
    elpasotx: {
      border: true,
      number_markers: true,
      shapefile: true,
    },
    elpaso2: {
      border: true,
      number_markers: true,
      shapefile: true,
    },
    utah: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      // find_unpainted: true,
      portal: {
        endpoint: 'https://portal.utah-mapping.org',
      },
    },
      'grand_county_2': {
        portal: {
          endpoint: 'https://portal.utah-mapping.org',
        },
        shapefile: true,
      },
    vermont: {
      number_markers: true,
      county_brush: true,
      multiyear: 2018,
      shapefile: true,
      // find_unpainted: true,
    },
    virginia: {
      number_markers: true,
      county_brush: true,
      // native_american: true,
      shapefile: true,
      // find_unpainted: true,
      load_coi: false,
      portal: {
        endpoint: 'https://portal.virginia-mapping.org',
      },
    },
    vabeach: {
      multiyear: 2018,
      number_markers: true,
      border: true,
      // find_unpainted: true,
    },
    washington: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
      contiguity: 2,
    },
    yakima_wa: {
      coalition: false,
    },
    kingcountywa: {
      border: true,
    },
    westvirginia: {
      multiyear: 2018,
      number_markers: true,
      county_brush: true,
      shapefile: true,
      find_unpainted: true,
    },
    wisconsin: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
      current_districts: true,
      school_districts: true,
      municipalities: true,
      contiguity: 2,
      portal: {
        endpoint: 'https://portal.wisconsin-mapping.org',
      },
      sideload: true,
    },
    wisconsin2020: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
      current_districts: true,
      school_districts: true,
      municipalities: true,
      contiguity: 2,
      portal: {
        endpoint: 'https://portal.wisconsin-mapping.org',
      },
      sideload: true,
    },
    wisco2019acs: {
      number_markers: true,
      county_brush: true,
      native_american: true,
      shapefile: true,
      find_unpainted: true,
      current_districts: true,
      school_districts: true,
      municipalities: true,
      contiguity: 2,
      portal: {
        endpoint: 'https://portal.wisconsin-mapping.org',
      },
      sideload: true,
    },
    wyoming: {
      multiyear: 2018,
      native_american: true,
      number_markers: true,
    }
  };
  return status[id] || {};
}

export function nested(st) {
  return [
    'alaska',
    'illinois',
    'iowa',
    'minnesota',
    'montana',
    'ohio',
    'oregon',
    'southdakota',
    'washington',
    'wisconsin'].includes(st);
}

export function one_cd(st) {
  return [
    'alaska',
    'alaska_blocks',
    'delaware',
    'montana',
    'northdakota',
    'southdakota',
    'vermont',
    'wyoming'].includes(st)
}

// takes a state and county FIPS code and gives you the county name
export function county_fips_to_name(fips, state) {
  fips = fips % 1000;
  const codes = {
      ohio:
      {
          1: "Adams",
          3: "Allen",
          5: "Ashland",
          7: "Ashtabula",
          9: "Athens",
          11: "Auglaize",
          13: "Belmont",
          15: "Brown",
          17: "Butler",
          19: "Carroll",
          21: "Champaign",
          23: "Clark",
          25: "Clermont",
          27: "Clinton",
          29: "Columbiana",
          31: "Coshocton",
          33: "Crawford",
          35: "Cuyahoga",
          37: "Darke",
          39: "Defiance",
          41: "Delaware",
          43: "Erie",
          45: "Fairfield",
          47: "Fayette",
          49: "Franklin",
          51: "Fulton",
          53: "Gallia",
          55: "Geauga",
          57: "Greene",
          59: "Guernsey",
          61: "Hamilton",
          63: "Hancock",
          65: "Hardin",
          67: "Harrison",
          69: "Henry",
          71: "Highland",
          73: "Hocking",
          75: "Holmes",
          77: "Huron",
          79: "Jackson",
          81: "Jefferson",
          83: "Knox",
          85: "Lake",
          87: "Lawrence",
          89: "Licking",
          91: "Logan",
          93: "Lorain",
          95: "Lucas",
          97: "Madison",
          99: "Mahoning",
          101: "Marion",
          103: "Medina",
          105: "Meigs",
          107: "Mercer",
          109: "Miami",
          111: "Monroe",
          113: "Montgomery",
          115: "Morgan",
          117: "Morrow",
          119: "Muskingum",
          121: "Noble",
          123: "Ottawa",
          125: "Paulding",
          127: "Perry",
          129: "Pickaway",
          131: "Pike",
          133: "Portage",
          135: "Preble",
          137: "Putnam",
          139: "Richland",
          141: "Ross",
          143: "Sandusky",
          145: "Scioto",
          147: "Seneca",
          149: "Shelby",
          151: "Stark",
          153: "Summit",
          155: "Trumbull",
          157: "Tuscarawas",
          159: "Union",
          161: "Van Wert",
          163: "Vinton",
          165: "Warren",
          167: "Washington",
          169: "Wayne",
          171: "Williams",
          173: "Wood",
          175: "Wyandot"
      },
      newyork:
      {
          1: "Albany",
          3: "Allegany",
          5: "Bronx",
          7: "Broome",
          9: "Cattaraugus",
          11: "Cayuga",
          13: "Chautauqua",
          15: "Chemung",
          17: "Chenango",
          19: "Clinton",
          21: "Columbia",
          23: "Cortland",
          25: "Delaware",
          27: "Dutchess",
          29: "Erie",
          31: "Essex",
          33: "Franklin",
          35: "Fulton",
          37: "Genesee",
          39: "Greene",
          41: "Hamilton",
          43: "Herkimer",
          45: "Jefferson",
          47: "Kings",
          49: "Lewis",
          51: "Livingston",
          53: "Madison",
          55: "Monroe",
          57: "Montgomery",
          59: "Nassau",
          61: "New York",
          63: "Niagara",
          65: "Oneida",
          67: "Onondaga",
          69: "Ontario",
          71: "Orange",
          73: "Orleans",
          75: "Oswego",
          77: "Otsego",
          79: "Putnam",
          81: "Queens",
          83: "Rensselaer",
          85: "Richmond",
          87: "Rockland",
          89: "St Lawrence",
          91: "Saratoga",
          93: "Schenectady",
          95: "Schoharie",
          97: "Schuyler",
          99: "Seneca",
          101: "Steuben",
          103: "Suffolk",
          105: "Sullivan",
          107: "Tioga",
          109: "Tompkins",
          111: "Ulster",
          113: "Warren",
          115: "Washington",
          117: "Wayne",
          119: "Westchester",
          121: "Wyoming",
          123: "Yates"
      },
      mississippi:
      {
          1: "Adams",
          3: "Alcorn",
          5: "Amite",
          7: "Attala",
          9: "Benton",
          11: "Bolivar",
          13: "Calhoun",
          15: "Carroll",
          17: "Chickasaw",
          19: "Choctaw",
          21: "Claiborne",
          23: "Clarke",
          25: "Clay",
          27: "Coahoma",
          29: "Copiah",
          31: "Covington",
          33: "De Soto",
          35: "Forrest",
          37: "Franklin",
          39: "George",
          41: "Greene",
          43: "Grenada",
          45: "Hancock",
          47: "Harrison",
          49: "Hinds",
          51: "Holmes",
          53: "Humphreys",
          55: "Issaquena",
          57: "Itawamba",
          59: "Jackson",
          61: "Jasper",
          63: "Jefferson",
          65: "Jefferson Davis",
          67: "Jones",
          69: "Kemper",
          71: "Lafayette",
          73: "Lamar",
          75: "Lauderdale",
          77: "Lawrence",
          79: "Leake",
          81: "Lee",
          83: "Leflore",
          85: "Lincoln",
          87: "Lowndes",
          89: "Madison",
          91: "Marion",
          93: "Marshall",
          95: "Monroe",
          97: "Montgomery",
          99: "Neshoba",
          101: "Newton",
          103: "Noxubee",
          105: "Oktibbeha",
          107: "Panola",
          109: "Pearl River",
          111: "Perry",
          113: "Pike",
          115: "Pontotoc",
          117: "Prentiss",
          119: "Quitman",
          121: "Rankin",
          123: "Scott",
          125: "Sharkey",
          127: "Simpson",
          129: "Smith",
          131: "Stone",
          133: "Sunflower",
          135: "Tallahatchie",
          137: "Tate",
          139: "Tippah",
          141: "Tishomingo",
          143: "Tunica",
          145: "Union",
          147: "Walthall",
          149: "Warren",
          151: "Washington",
          153: "Wayne",
          155: "Webster",
          157: "Wilkinson",
          159: "Winston",
          161: "Yalobusha",
          163: "Yazoo"
      },
      massachusetts:
      {
          1: "Barnstable",
          3: "Berkshire",
          5: "Bristol",
          7: "Dukes",
          9: "Essex",
          11: "Franklin",
          13: "Hampden",
          15: "Hampshire",
          17: "Middlesex",
          19: "Nantucket",
          21: "Norfolk",
          23: "Plymouth",
          25: "Suffolk",
          27: "Worcester"
      },
      northdakota:
      {
          1: "Adams",
          3: "Barnes",
          5: "Benson",
          7: "Billings",
          9: "Bottineau",
          11: "Bowman",
          13: "Burke",
          15: "Burleigh",
          17: "Cass",
          19: "Cavalier",
          21: "Dickey",
          23: "Divide",
          25: "Dunn",
          27: "Eddy",
          29: "Emmons",
          31: "Foster",
          33: "Golden Valley",
          35: "Grand Forks",
          37: "Grant",
          39: "Griggs",
          41: "Hettinger",
          43: "Kidder",
          45: "La Moure",
          47: "Logan",
          49: "McHenry",
          51: "McIntosh",
          53: "McKenzie",
          55: "McLean",
          57: "Mercer",
          59: "Morton",
          61: "Mountrail",
          63: "Nelson",
          65: "Oliver",
          67: "Pembina",
          69: "Pierce",
          71: "Ramsey",
          73: "Ransom",
          75: "Renville",
          77: "Richland",
          79: "Rolette",
          81: "Sargent",
          83: "Sheridan",
          85: "Sioux",
          87: "Slope",
          89: "Stark",
          91: "Steele",
          93: "Stutsman",
          95: "Towner",
          97: "Traill",
          99: "Walsh",
          101: "Ward",
          103: "Wells",
          105: "Williams"
      },
      nebraska:
      {
          1: "Adams",
          3: "Antelope",
          5: "Arthur",
          7: "Banner",
          9: "Blaine",
          11: "Boone",
          13: "Box Butte",
          15: "Boyd",
          17: "Brown",
          19: "Buffalo",
          21: "Burt",
          23: "Butler",
          25: "Cass",
          27: "Cedar",
          29: "Chase",
          31: "Cherry",
          33: "Cheyenne",
          35: "Clay",
          37: "Colfax",
          39: "Cuming",
          41: "Custer",
          43: "Dakota",
          45: "Dawes",
          47: "Dawson",
          49: "Deuel",
          51: "Dixon",
          53: "Dodge",
          55: "Douglas",
          57: "Dundy",
          59: "Fillmore",
          61: "Franklin",
          63: "Frontier",
          65: "Furnas",
          67: "Gage",
          69: "Garden",
          71: "Garfield",
          73: "Gosper",
          75: "Grant",
          77: "Greeley",
          79: "Hall",
          81: "Hamilton",
          83: "Harlan",
          85: "Hayes",
          87: "Hitchcock",
          89: "Holt",
          91: "Hooker",
          93: "Howard",
          95: "Jefferson",
          97: "Johnson",
          99: "Kearney",
          101: "Keith",
          103: "Keya Paha",
          105: "Kimball",
          107: "Knox",
          109: "Lancaster",
          111: "Lincoln",
          113: "Logan",
          115: "Loup",
          117: "McPherson",
          119: "Madison",
          121: "Merrick",
          123: "Morrill",
          125: "Nance",
          127: "Nemaha",
          129: "Nuckolls",
          131: "Otoe",
          133: "Pawnee",
          135: "Perkins",
          137: "Phelps",
          139: "Pierce",
          141: "Platte",
          143: "Polk",
          145: "Red Willow",
          147: "Richardson",
          149: "Rock",
          151: "Saline",
          153: "Sarpy",
          155: "Saunders",
          157: "Scotts Bluff",
          159: "Seward",
          161: "Sheridan",
          163: "Sherman",
          165: "Sioux",
          167: "Stanton",
          169: "Thayer",
          171: "Thomas",
          173: "Thurston",
          175: "Valley",
          177: "Washington",
          179: "Wayne",
          181: "Webster",
          183: "Wheeler",
          185: "York"
      },
      nevada:
      {
          1: "Churchill",
          3: "Clark",
          5: "Douglas",
          7: "Elko",
          9: "Esmeralda",
          11: "Eureka",
          13: "Humboldt",
          15: "Lander",
          17: "Lincoln",
          19: "Lyon",
          21: "Mineral",
          23: "Nye",
          27: "Pershing",
          29: "Storey",
          31: "Washoe",
          33: "White Pine",
          510: "Carson City"
      },
      virginislands:
      {
          10: "St. Croix",
          20: "St. John",
          30: "St. Thomas"
      },
      alabama:
      {
          1: "Autauga",
          3: "Baldwin",
          5: "Barbour",
          7: "Bibb",
          9: "Blount",
          11: "Bullock",
          13: "Butler",
          15: "Calhoun",
          17: "Chambers",
          19: "Cherokee",
          21: "Chilton",
          23: "Choctaw",
          25: "Clarke",
          27: "Clay",
          29: "Cleburne",
          31: "Coffee",
          33: "Colbert",
          35: "Conecuh",
          37: "Coosa",
          39: "Covington",
          41: "Crenshaw",
          43: "Cullman",
          45: "Dale",
          47: "Dallas",
          49: "De Kalb",
          51: "Elmore",
          53: "Escambia",
          55: "Etowah",
          57: "Fayette",
          59: "Franklin",
          61: "Geneva",
          63: "Greene",
          65: "Hale",
          67: "Henry",
          69: "Houston",
          71: "Jackson",
          73: "Jefferson",
          75: "Lamar",
          77: "Lauderdale",
          79: "Lawrence",
          81: "Lee",
          83: "Limestone",
          85: "Lowndes",
          87: "Macon",
          89: "Madison",
          91: "Marengo",
          93: "Marion",
          95: "Marshall",
          97: "Mobile",
          99: "Monroe",
          101: "Montgomery",
          103: "Morgan",
          105: "Perry",
          107: "Pickens",
          109: "Pike",
          111: "Randolph",
          113: "Russell",
          115: "St Clair",
          117: "Shelby",
          119: "Sumter",
          121: "Talladega",
          123: "Tallapoosa",
          125: "Tuscaloosa",
          127: "Walker",
          129: "Washington",
          131: "Wilcox",
          133: "Winston"
      },
      delaware:
      {
          1: "Kent",
          3: "New Castle",
          5: "Sussex"
      },
      virginia:
      {
          1: "Accomack",
          3: "Albemarle",
          5: "Alleghany",
          7: "Amelia",
          9: "Amherst",
          11: "Appomattox",
          13: "Arlington",
          15: "Augusta",
          17: "Bath",
          19: "Bedford",
          21: "Bland",
          23: "Botetourt",
          25: "Brunswick",
          27: "Buchanan",
          29: "Buckingham",
          31: "Campbell",
          33: "Caroline",
          35: "Carroll",
          36: "Charles City",
          37: "Charlotte",
          41: "Chesterfield",
          43: "Clarke",
          45: "Craig",
          47: "Culpeper",
          49: "Cumberland",
          51: "Dickenson",
          53: "Dinwiddie",
          57: "Essex",
          59: "Fairfax",
          61: "Fauquier",
          63: "Floyd",
          65: "Fluvanna",
          67: "Franklin",
          69: "Frederick",
          71: "Giles",
          73: "Gloucester",
          75: "Goochland",
          77: "Grayson",
          79: "Greene",
          81: "Greensville",
          83: "Halifax",
          85: "Hanover",
          87: "Henrico",
          89: "Henry",
          91: "Highland",
          93: "Isle of Wight",
          95: "James City",
          97: "King and Queen",
          99: "King George",
          101: "King William",
          103: "Lancaster",
          105: "Lee",
          107: "Loudoun",
          109: "Louisa",
          111: "Lunenburg",
          113: "Madison",
          115: "Mathews",
          117: "Mecklenburg",
          119: "Middlesex",
          121: "Montgomery",
          125: "Nelson",
          127: "New Kent",
          131: "Northampton",
          133: "Northumberland",
          135: "Nottoway",
          137: "Orange",
          139: "Page",
          141: "Patrick",
          143: "Pittsylvania",
          145: "Powhatan",
          147: "Prince Edward",
          149: "Prince George",
          153: "Prince William",
          155: "Pulaski",
          157: "Rappahannock",
          159: "Richmond",
          161: "Roanoke",
          163: "Rockbridge",
          165: "Rockingham",
          167: "Russell",
          169: "Scott",
          171: "Shenandoah",
          173: "Smyth",
          175: "Southampton",
          177: "Spotsylvania",
          179: "Stafford",
          181: "Surry",
          183: "Sussex",
          185: "Tazewell",
          187: "Warren",
          191: "Washington",
          193: "Westmoreland",
          195: "Wise",
          197: "Wythe",
          199: "York",
          510: "Alexandria City",
          515: "Bedford City",
          520: "Bristol City",
          530: "Buena Vista City",
          540: "Charlottesville City",
          550: "Chesapeake City",
          560: "Clifton Forge City",
          570: "Colonial Heights Cit",
          580: "Covington City",
          590: "Danville City",
          595: "Emporia City",
          600: "Fairfax City",
          610: "Falls Church City",
          620: "Franklin City",
          630: "Fredericksburg City",
          640: "Galax City",
          650: "Hampton City",
          660: "Harrisonburg City",
          670: "Hopewell City",
          678: "Lexington City",
          680: "Lynchburg City",
          683: "Manassas City",
          685: "Manassas Park City",
          690: "Martinsville City",
          700: "Newport News City",
          710: "Norfolk City",
          720: "Norton City",
          730: "Petersburg City",
          735: "Poquoson City",
          740: "Portsmouth City",
          750: "Radford City",
          760: "Richmond City",
          770: "Roanoke City",
          775: "Salem City",
          780: "South Boston City",
          790: "Staunton City",
          800: "Suffolk City",
          810: "Virginia Beach City",
          820: "Waynesboro City",
          830: "Williamsburg City",
          840: "Winchester City"
      },
      vermont:
      {
          1: "Addison",
          3: "Bennington",
          5: "Caledonia",
          7: "Chittenden",
          9: "Essex",
          11: "Franklin",
          13: "Grand Isle",
          15: "Lamoille",
          17: "Orange",
          19: "Orleans",
          21: "Rutland",
          23: "Washington",
          25: "Windham",
          27: "Windsor"
      },
      southdakota:
      {
          3: "Aurora",
          5: "Beadle",
          7: "Bennett",
          9: "Bon Homme",
          11: "Brookings",
          13: "Brown",
          15: "Brule",
          17: "Buffalo",
          19: "Butte",
          21: "Campbell",
          23: "Charles Mix",
          25: "Clark",
          27: "Clay",
          29: "Codington",
          31: "Corson",
          33: "Custer",
          35: "Davison",
          37: "Day",
          39: "Deuel",
          41: "Dewey",
          43: "Douglas",
          45: "Edmunds",
          47: "Fall River",
          49: "Faulk",
          51: "Grant",
          53: "Gregory",
          55: "Haakon",
          57: "Hamlin",
          59: "Hand",
          61: "Hanson",
          63: "Harding",
          65: "Hughes",
          67: "Hutchinson",
          69: "Hyde",
          71: "Jackson",
          73: "Jerauld",
          75: "Jones",
          77: "Kingsbury",
          79: "Lake",
          81: "Lawrence",
          83: "Lincoln",
          85: "Lyman",
          87: "McCook",
          89: "McPherson",
          91: "Marshall",
          93: "Meade",
          95: "Mellette",
          97: "Miner",
          99: "Minnehaha",
          101: "Moody",
          103: "Pennington",
          105: "Perkins",
          107: "Potter",
          109: "Roberts",
          111: "Sanborn",
          113: "Shannon",
          115: "Spink",
          117: "Stanley",
          119: "Sully",
          121: "Todd",
          123: "Tripp",
          125: "Turner",
          127: "Union",
          129: "Walworth",
          135: "Yankton",
          137: "Ziebach"
      },
      wyoming:
      {
          1: "Albany",
          3: "Big Horn",
          5: "Campbell",
          7: "Carbon",
          9: "Converse",
          11: "Crook",
          13: "Fremont",
          15: "Goshen",
          17: "Hot Springs",
          19: "Johnson",
          21: "Laramie",
          23: "Lincoln",
          25: "Natrona",
          27: "Niobrara",
          29: "Park",
          31: "Platte",
          33: "Sheridan",
          35: "Sublette",
          37: "Sweetwater",
          39: "Teton",
          41: "Uinta",
          43: "Washakie",
          45: "Weston"
      },
      alaska:
      {
          13: "Aleutians East",
          16: "Aleutians West",
          20: "Anchorage",
          50: "Bethel",
          60: "Bristol Bay",
          68: "Denali",
          70: "Dillingham",
          90: "Fairbanks North Star",
          100: "Haines",
          110: "Juneau",
          122: "Kenai Peninsula",
          130: "Ketchikan Gateway",
          150: "Kodiak Island",
          164: "Lake and Peninsula",
          170: "Matanuska-Susitna",
          180: "Nome",
          185: "North Slope",
          188: "Northwest Arctic",
          201: "Prince of Wales-Outer Ketchikan",
          220: "Sitka",
          232: "Skagway-Hoonah-Angoon",
          240: "Southeast Fairbanks",
          261: "Valdez-Cordova",
          270: "Wade Hampton",
          280: "Wrangell-Petersburg",
          282: "Yakutat",
          290: "Yukon-Koyukuk"
      },
      oregon:
      {
          1: "Baker",
          3: "Benton",
          5: "Clackamas",
          7: "Clatsop",
          9: "Columbia",
          11: "Coos",
          13: "Crook",
          15: "Curry",
          17: "Deschutes",
          19: "Douglas",
          21: "Gilliam",
          23: "Grant",
          25: "Harney",
          27: "Hood River",
          29: "Jackson",
          31: "Jefferson",
          33: "Josephine",
          35: "Klamath",
          37: "Lake",
          39: "Lane",
          41: "Lincoln",
          43: "Linn",
          45: "Malheur",
          47: "Marion",
          49: "Morrow",
          51: "Multnomah",
          53: "Polk",
          55: "Sherman",
          57: "Tillamook",
          59: "Umatilla",
          61: "Union",
          63: "Wallowa",
          65: "Wasco",
          67: "Washington",
          69: "Wheeler",
          71: "Yamhill"
      },
      wisconsin:
      {
          1: "Adams",
          3: "Ashland",
          5: "Barron",
          7: "Bayfield",
          9: "Brown",
          11: "Buffalo",
          13: "Burnett",
          15: "Calumet",
          17: "Chippewa",
          19: "Clark",
          21: "Columbia",
          23: "Crawford",
          25: "Dane",
          27: "Dodge",
          29: "Door",
          31: "Douglas",
          33: "Dunn",
          35: "Eau Claire",
          37: "Florence",
          39: "Fond du Lac",
          41: "Forest",
          43: "Grant",
          45: "Green",
          47: "Green Lake",
          49: "Iowa",
          51: "Iron",
          53: "Jackson",
          55: "Jefferson",
          57: "Juneau",
          59: "Kenosha",
          61: "Kewaunee",
          63: "La Crosse",
          65: "Lafayette",
          67: "Langlade",
          69: "Lincoln",
          71: "Manitowoc",
          73: "Marathon",
          75: "Marinette",
          77: "Marquette",
          78: "Menominee",
          79: "Milwaukee",
          81: "Monroe",
          83: "Oconto",
          85: "Oneida",
          87: "Outagamie",
          89: "Ozaukee",
          91: "Pepin",
          93: "Pierce",
          95: "Polk",
          97: "Portage",
          99: "Price",
          101: "Racine",
          103: "Richland",
          105: "Rock",
          107: "Rusk",
          109: "St Croix",
          111: "Sauk",
          113: "Sawyer",
          115: "Shawano",
          117: "Sheboygan",
          119: "Taylor",
          121: "Trempealeau",
          123: "Vernon",
          125: "Vilas",
          127: "Walworth",
          129: "Washburn",
          131: "Washington",
          133: "Waukesha",
          135: "Waupaca",
          137: "Waushara",
          139: "Winnebago",
          141: "Wood"
      },
      districtofcolumbia:
      {
          1: "Washington"
      },
      georgia:
      {
          1: "Appling",
          3: "Atkinson",
          5: "Bacon",
          7: "Baker",
          9: "Baldwin",
          11: "Banks",
          13: "Barrow",
          15: "Bartow",
          17: "Ben Hill",
          19: "Berrien",
          21: "Bibb",
          23: "Bleckley",
          25: "Brantley",
          27: "Brooks",
          29: "Bryan",
          31: "Bulloch",
          33: "Burke",
          35: "Butts",
          37: "Calhoun",
          39: "Camden",
          43: "Candler",
          45: "Carroll",
          47: "Catoosa",
          49: "Charlton",
          51: "Chatham",
          53: "Chattahoochee",
          55: "Chattooga",
          57: "Cherokee",
          59: "Clarke",
          61: "Clay",
          63: "Clayton",
          65: "Clinch",
          67: "Cobb",
          69: "Coffee",
          71: "Colquitt",
          73: "Columbia",
          75: "Cook",
          77: "Coweta",
          79: "Crawford",
          81: "Crisp",
          83: "Dade",
          85: "Dawson",
          87: "Decatur",
          89: "De Kalb",
          91: "Dodge",
          93: "Dooly",
          95: "Dougherty",
          97: "Douglas",
          99: "Early",
          101: "Echols",
          103: "Effingham",
          105: "Elbert",
          107: "Emanuel",
          109: "Evans",
          111: "Fannin",
          113: "Fayette",
          115: "Floyd",
          117: "Forsyth",
          119: "Franklin",
          121: "Fulton",
          123: "Gilmer",
          125: "Glascock",
          127: "Glynn",
          129: "Gordon",
          131: "Grady",
          133: "Greene",
          135: "Gwinnett",
          137: "Habersham",
          139: "Hall",
          141: "Hancock",
          143: "Haralson",
          145: "Harris",
          147: "Hart",
          149: "Heard",
          151: "Henry",
          153: "Houston",
          155: "Irwin",
          157: "Jackson",
          159: "Jasper",
          161: "Jeff Davis",
          163: "Jefferson",
          165: "Jenkins",
          167: "Johnson",
          169: "Jones",
          171: "Lamar",
          173: "Lanier",
          175: "Laurens",
          177: "Lee",
          179: "Liberty",
          181: "Lincoln",
          183: "Long",
          185: "Lowndes",
          187: "Lumpkin",
          189: "McDuffie",
          191: "McIntosh",
          193: "Macon",
          195: "Madison",
          197: "Marion",
          199: "Meriwether",
          201: "Miller",
          205: "Mitchell",
          207: "Monroe",
          209: "Montgomery",
          211: "Morgan",
          213: "Murray",
          215: "Muscogee",
          217: "Newton",
          219: "Oconee",
          221: "Oglethorpe",
          223: "Paulding",
          225: "Peach",
          227: "Pickens",
          229: "Pierce",
          231: "Pike",
          233: "Polk",
          235: "Pulaski",
          237: "Putnam",
          239: "Quitman",
          241: "Rabun",
          243: "Randolph",
          245: "Richmond",
          247: "Rockdale",
          249: "Schley",
          251: "Screven",
          253: "Seminole",
          255: "Spalding",
          257: "Stephens",
          259: "Stewart",
          261: "Sumter",
          263: "Talbot",
          265: "Taliaferro",
          267: "Tattnall",
          269: "Taylor",
          271: "Telfair",
          273: "Terrell",
          275: "Thomas",
          277: "Tift",
          279: "Toombs",
          281: "Towns",
          283: "Treutlen",
          285: "Troup",
          287: "Turner",
          289: "Twiggs",
          291: "Union",
          293: "Upson",
          295: "Walker",
          297: "Walton",
          299: "Ware",
          301: "Warren",
          303: "Washington",
          305: "Wayne",
          307: "Webster",
          309: "Wheeler",
          311: "White",
          313: "Whitfield",
          315: "Wilcox",
          317: "Wilkes",
          319: "Wilkinson",
          321: "Worth"
      },
      westvirginia:
      {
          1: "Barbour",
          3: "Berkeley",
          5: "Boone",
          7: "Braxton",
          9: "Brooke",
          11: "Cabell",
          13: "Calhoun",
          15: "Clay",
          17: "Doddridge",
          19: "Fayette",
          21: "Gilmer",
          23: "Grant",
          25: "Greenbrier",
          27: "Hampshire",
          29: "Hancock",
          31: "Hardy",
          33: "Harrison",
          35: "Jackson",
          37: "Jefferson",
          39: "Kanawha",
          41: "Lewis",
          43: "Lincoln",
          45: "Logan",
          47: "McDowell",
          49: "Marion",
          51: "Marshall",
          53: "Mason",
          55: "Mercer",
          57: "Mineral",
          59: "Mingo",
          61: "Monongalia",
          63: "Monroe",
          65: "Morgan",
          67: "Nicholas",
          69: "Ohio",
          71: "Pendleton",
          73: "Pleasants",
          75: "Pocahontas",
          77: "Preston",
          79: "Putnam",
          81: "Raleigh",
          83: "Randolph",
          85: "Ritchie",
          87: "Roane",
          89: "Summers",
          91: "Taylor",
          93: "Tucker",
          95: "Tyler",
          97: "Upshur",
          99: "Wayne",
          101: "Webster",
          103: "Wetzel",
          105: "Wirt",
          107: "Wood",
          109: "Wyoming"
      },
      newmexico:
      {
          1: "Bernalillo",
          3: "Catron",
          5: "Chaves",
          6: "Cibola",
          7: "Colfax",
          9: "Curry",
          11: "De Baca",
          13: "Dona Ana",
          15: "Eddy",
          17: "Grant",
          19: "Guadalupe",
          21: "Harding",
          23: "Hidalgo",
          25: "Lea",
          27: "Lincoln",
          28: "Los Alamos",
          29: "Luna",
          31: "McKinley",
          33: "Mora",
          35: "Otero",
          37: "Quay",
          39: "Rio Arriba",
          41: "Roosevelt",
          43: "Sandoval",
          45: "San Juan",
          47: "San Miguel",
          49: "Santa Fe",
          51: "Sierra",
          53: "Socorro",
          55: "Taos",
          57: "Torrance",
          59: "Union",
          61: "Valencia"
      },
      kansas:
      {
          1: "Allen",
          3: "Anderson",
          5: "Atchison",
          7: "Barber",
          9: "Barton",
          11: "Bourbon",
          13: "Brown",
          15: "Butler",
          17: "Chase",
          19: "Chautauqua",
          21: "Cherokee",
          23: "Cheyenne",
          25: "Clark",
          27: "Clay",
          29: "Cloud",
          31: "Coffey",
          33: "Comanche",
          35: "Cowley",
          37: "Crawford",
          39: "Decatur",
          41: "Dickinson",
          43: "Doniphan",
          45: "Douglas",
          47: "Edwards",
          49: "Elk",
          51: "Ellis",
          53: "Ellsworth",
          55: "Finney",
          57: "Ford",
          59: "Franklin",
          61: "Geary",
          63: "Gove",
          65: "Graham",
          67: "Grant",
          69: "Gray",
          71: "Greeley",
          73: "Greenwood",
          75: "Hamilton",
          77: "Harper",
          79: "Harvey",
          81: "Haskell",
          83: "Hodgeman",
          85: "Jackson",
          87: "Jefferson",
          89: "Jewell",
          91: "Johnson",
          93: "Kearny",
          95: "Kingman",
          97: "Kiowa",
          99: "Labette",
          101: "Lane",
          103: "Leavenworth",
          105: "Lincoln",
          107: "Linn",
          109: "Logan",
          111: "Lyon",
          113: "McPherson",
          115: "Marion",
          117: "Marshall",
          119: "Meade",
          121: "Miami",
          123: "Mitchell",
          125: "Montgomery",
          127: "Morris",
          129: "Morton",
          131: "Nemaha",
          133: "Neosho",
          135: "Ness",
          137: "Norton",
          139: "Osage",
          141: "Osborne",
          143: "Ottawa",
          145: "Pawnee",
          147: "Phillips",
          149: "Pottawatomie",
          151: "Pratt",
          153: "Rawlins",
          155: "Reno",
          157: "Republic",
          159: "Rice",
          161: "Riley",
          163: "Rooks",
          165: "Rush",
          167: "Russell",
          169: "Saline",
          171: "Scott",
          173: "Sedgwick",
          175: "Seward",
          177: "Shawnee",
          179: "Sheridan",
          181: "Sherman",
          183: "Smith",
          185: "Stafford",
          187: "Stanton",
          189: "Stevens",
          191: "Sumner",
          193: "Thomas",
          195: "Trego",
          197: "Wabaunsee",
          199: "Wallace",
          201: "Washington",
          203: "Wichita",
          205: "Wilson",
          207: "Woodson",
          209: "Wyandotte"
      },
      louisiana:
      {
          1: "Acadia",
          3: "Allen",
          5: "Ascension",
          7: "Assumption",
          9: "Avoyelles",
          11: "Beauregard",
          13: "Bienville",
          15: "Bossier",
          17: "Caddo",
          19: "Calcasieu",
          21: "Caldwell",
          23: "Cameron",
          25: "Catahoula",
          27: "Claiborne",
          29: "Concordia",
          31: "De Soto",
          33: "East Baton Rouge",
          35: "East Carroll",
          37: "East Feliciana",
          39: "Evangeline",
          41: "Franklin",
          43: "Grant",
          45: "Iberia",
          47: "Iberville",
          49: "Jackson",
          51: "Jefferson",
          53: "Jefferson Davis",
          55: "Lafayette",
          57: "Lafourche",
          59: "La Salle",
          61: "Lincoln",
          63: "Livingston",
          65: "Madison",
          67: "Morehouse",
          69: "Natchitoches",
          71: "Orleans",
          73: "Ouachita",
          75: "Plaquemines",
          77: "Pointe Coupee",
          79: "Rapides",
          81: "Red River",
          83: "Richland",
          85: "Sabine",
          87: "St Bernard",
          89: "St Charles",
          91: "St Helena",
          93: "St James",
          95: "St John the Baptist",
          97: "St Landry",
          99: "St Martin",
          101: "St Mary",
          103: "St Tammany",
          105: "Tangipahoa",
          107: "Tensas",
          109: "Terrebonne",
          111: "Union",
          113: "Vermilion",
          115: "Vernon",
          117: "Washington",
          119: "Webster",
          121: "West Baton Rouge",
          123: "West Carroll",
          125: "West Feliciana",
          127: "Winn"
      },
      hawaii:
      {
          1: "Hawaii",
          3: "Honolulu",
          7: "Kauai",
          9: "Maui"
      },
      southcarolina:
      {
          1: "Abbeville",
          3: "Aiken",
          5: "Allendale",
          7: "Anderson",
          9: "Bamberg",
          11: "Barnwell",
          13: "Beaufort",
          15: "Berkeley",
          17: "Calhoun",
          19: "Charleston",
          21: "Cherokee",
          23: "Chester",
          25: "Chesterfield",
          27: "Clarendon",
          29: "Colleton",
          31: "Darlington",
          33: "Dillon",
          35: "Dorchester",
          37: "Edgefield",
          39: "Fairfield",
          41: "Florence",
          43: "Georgetown",
          45: "Greenville",
          47: "Greenwood",
          49: "Hampton",
          51: "Horry",
          53: "Jasper",
          55: "Kershaw",
          57: "Lancaster",
          59: "Laurens",
          61: "Lee",
          63: "Lexington",
          65: "McCormick",
          67: "Marion",
          69: "Marlboro",
          71: "Newberry",
          73: "Oconee",
          75: "Orangeburg",
          77: "Pickens",
          79: "Richland",
          81: "Saluda",
          83: "Spartanburg",
          85: "Sumter",
          87: "Union",
          89: "Williamsburg",
          91: "York"
      },
      pennsylvania:
      {
          1: "Adams",
          3: "Allegheny",
          5: "Armstrong",
          7: "Beaver",
          9: "Bedford",
          11: "Berks",
          13: "Blair",
          15: "Bradford",
          17: "Bucks",
          19: "Butler",
          21: "Cambria",
          23: "Cameron",
          25: "Carbon",
          27: "Centre",
          29: "Chester",
          31: "Clarion",
          33: "Clearfield",
          35: "Clinton",
          37: "Columbia",
          39: "Crawford",
          41: "Cumberland",
          43: "Dauphin",
          45: "Delaware",
          47: "Elk",
          49: "Erie",
          51: "Fayette",
          53: "Forest",
          55: "Franklin",
          57: "Fulton",
          59: "Greene",
          61: "Huntingdon",
          63: "Indiana",
          65: "Jefferson",
          67: "Juniata",
          69: "Lackawanna",
          71: "Lancaster",
          73: "Lawrence",
          75: "Lebanon",
          77: "Lehigh",
          79: "Luzerne",
          81: "Lycoming",
          83: "McKean",
          85: "Mercer",
          87: "Mifflin",
          89: "Monroe",
          91: "Montgomery",
          93: "Montour",
          95: "Northampton",
          97: "Northumberland",
          99: "Perry",
          101: "Philadelphia",
          103: "Pike",
          105: "Potter",
          107: "Schuylkill",
          109: "Snyder",
          111: "Somerset",
          113: "Sullivan",
          115: "Susquehanna",
          117: "Tioga",
          119: "Union",
          121: "Venango",
          123: "Warren",
          125: "Washington",
          127: "Wayne",
          129: "Westmoreland",
          131: "Wyoming",
          133: "York"
      },
      washington:
      {
          1: "Adams",
          3: "Asotin",
          5: "Benton",
          7: "Chelan",
          9: "Clallam",
          11: "Clark",
          13: "Columbia",
          15: "Cowlitz",
          17: "Douglas",
          19: "Ferry",
          21: "Franklin",
          23: "Garfield",
          25: "Grant",
          27: "Grays Harbor",
          29: "Island",
          31: "Jefferson",
          33: "King",
          35: "Kitsap",
          37: "Kittitas",
          39: "Klickitat",
          41: "Lewis",
          43: "Lincoln",
          45: "Mason",
          47: "Okanogan",
          49: "Pacific",
          51: "Pend Oreille",
          53: "Pierce",
          55: "San Juan",
          57: "Skagit",
          59: "Skamania",
          61: "Snohomish",
          63: "Spokane",
          65: "Stevens",
          67: "Thurston",
          69: "Wahkiakum",
          71: "Walla Walla",
          73: "Whatcom",
          75: "Whitman",
          77: "Yakima"
      },
      texas:
      {
          1: "Anderson",
          3: "Andrews",
          5: "Angelina",
          7: "Aransas",
          9: "Archer",
          11: "Armstrong",
          13: "Atascosa",
          15: "Austin",
          17: "Bailey",
          19: "Bandera",
          21: "Bastrop",
          23: "Baylor",
          25: "Bee",
          27: "Bell",
          29: "Bexar",
          31: "Blanco",
          33: "Borden",
          35: "Bosque",
          37: "Bowie",
          39: "Brazoria",
          41: "Brazos",
          43: "Brewster",
          45: "Briscoe",
          47: "Brooks",
          49: "Brown",
          51: "Burleson",
          53: "Burnet",
          55: "Caldwell",
          57: "Calhoun",
          59: "Callahan",
          61: "Cameron",
          63: "Camp",
          65: "Carson",
          67: "Cass",
          69: "Castro",
          71: "Chambers",
          73: "Cherokee",
          75: "Childress",
          77: "Clay",
          79: "Cochran",
          81: "Coke",
          83: "Coleman",
          85: "Collin",
          87: "Collingsworth",
          89: "Colorado",
          91: "Comal",
          93: "Comanche",
          95: "Concho",
          97: "Cooke",
          99: "Coryell",
          101: "Cottle",
          103: "Crane",
          105: "Crockett",
          107: "Crosby",
          109: "Culberson",
          111: "Dallam",
          113: "Dallas",
          115: "Dawson",
          117: "Deaf Smith",
          119: "Delta",
          121: "Denton",
          123: "De Witt",
          125: "Dickens",
          127: "Dimmit",
          129: "Donley",
          131: "Duval",
          133: "Eastland",
          135: "Ector",
          137: "Edwards",
          139: "Ellis",
          141: "El Paso",
          143: "Erath",
          145: "Falls",
          147: "Fannin",
          149: "Fayette",
          151: "Fisher",
          153: "Floyd",
          155: "Foard",
          157: "Fort Bend",
          159: "Franklin",
          161: "Freestone",
          163: "Frio",
          165: "Gaines",
          167: "Galveston",
          169: "Garza",
          171: "Gillespie",
          173: "Glasscock",
          175: "Goliad",
          177: "Gonzales",
          179: "Gray",
          181: "Grayson",
          183: "Gregg",
          185: "Grimes",
          187: "Guadalupe",
          189: "Hale",
          191: "Hall",
          193: "Hamilton",
          195: "Hansford",
          197: "Hardeman",
          199: "Hardin",
          201: "Harris",
          203: "Harrison",
          205: "Hartley",
          207: "Haskell",
          209: "Hays",
          211: "Hemphill",
          213: "Henderson",
          215: "Hidalgo",
          217: "Hill",
          219: "Hockley",
          221: "Hood",
          223: "Hopkins",
          225: "Houston",
          227: "Howard",
          229: "Hudspeth",
          231: "Hunt",
          233: "Hutchinson",
          235: "Irion",
          237: "Jack",
          239: "Jackson",
          241: "Jasper",
          243: "Jeff Davis",
          245: "Jefferson",
          247: "Jim Hogg",
          249: "Jim Wells",
          251: "Johnson",
          253: "Jones",
          255: "Karnes",
          257: "Kaufman",
          259: "Kendall",
          261: "Kenedy",
          263: "Kent",
          265: "Kerr",
          267: "Kimble",
          269: "King",
          271: "Kinney",
          273: "Kleberg",
          275: "Knox",
          277: "Lamar",
          279: "Lamb",
          281: "Lampasas",
          283: "La Salle",
          285: "Lavaca",
          287: "Lee",
          289: "Leon",
          291: "Liberty",
          293: "Limestone",
          295: "Lipscomb",
          297: "Live Oak",
          299: "Llano",
          301: "Loving",
          303: "Lubbock",
          305: "Lynn",
          307: "McCulloch",
          309: "McLennan",
          311: "McMullen",
          313: "Madison",
          315: "Marion",
          317: "Martin",
          319: "Mason",
          321: "Matagorda",
          323: "Maverick",
          325: "Medina",
          327: "Menard",
          329: "Midland",
          331: "Milam",
          333: "Mills",
          335: "Mitchell",
          337: "Montague",
          339: "Montgomery",
          341: "Moore",
          343: "Morris",
          345: "Motley",
          347: "Nacogdoches",
          349: "Navarro",
          351: "Newton",
          353: "Nolan",
          355: "Nueces",
          357: "Ochiltree",
          359: "Oldham",
          361: "Orange",
          363: "Palo Pinto",
          365: "Panola",
          367: "Parker",
          369: "Parmer",
          371: "Pecos",
          373: "Polk",
          375: "Potter",
          377: "Presidio",
          379: "Rains",
          381: "Randall",
          383: "Reagan",
          385: "Real",
          387: "Red River",
          389: "Reeves",
          391: "Refugio",
          393: "Roberts",
          395: "Robertson",
          397: "Rockwall",
          399: "Runnels",
          401: "Rusk",
          403: "Sabine",
          405: "San Augustine",
          407: "San Jacinto",
          409: "San Patricio",
          411: "San Saba",
          413: "Schleicher",
          415: "Scurry",
          417: "Shackelford",
          419: "Shelby",
          421: "Sherman",
          423: "Smith",
          425: "Somervell",
          427: "Starr",
          429: "Stephens",
          431: "Sterling",
          433: "Stonewall",
          435: "Sutton",
          437: "Swisher",
          439: "Tarrant",
          441: "Taylor",
          443: "Terrell",
          445: "Terry",
          447: "Throckmorton",
          449: "Titus",
          451: "Tom Green",
          453: "Travis",
          455: "Trinity",
          457: "Tyler",
          459: "Upshur",
          461: "Upton",
          463: "Uvalde",
          465: "Val Verde",
          467: "Van Zandt",
          469: "Victoria",
          471: "Walker",
          473: "Waller",
          475: "Ward",
          477: "Washington",
          479: "Webb",
          481: "Wharton",
          483: "Wheeler",
          485: "Wichita",
          487: "Wilbarger",
          489: "Willacy",
          491: "Williamson",
          493: "Wilson",
          495: "Winkler",
          497: "Wise",
          499: "Wood",
          501: "Yoakum",
          503: "Young",
          505: "Zapata",
          507: "Zavala"
      },
      arkansas:
      {
          1: "Arkansas",
          3: "Ashley",
          5: "Baxter",
          7: "Benton",
          9: "Boone",
          11: "Bradley",
          13: "Calhoun",
          15: "Carroll",
          17: "Chicot",
          19: "Clark",
          21: "Clay",
          23: "Cleburne",
          25: "Cleveland",
          27: "Columbia",
          29: "Conway",
          31: "Craighead",
          33: "Crawford",
          35: "Crittenden",
          37: "Cross",
          39: "Dallas",
          41: "Desha",
          43: "Drew",
          45: "Faulkner",
          47: "Franklin",
          49: "Fulton",
          51: "Garland",
          53: "Grant",
          55: "Greene",
          57: "Hempstead",
          59: "Hot Spring",
          61: "Howard",
          63: "Independence",
          65: "Izard",
          67: "Jackson",
          69: "Jefferson",
          71: "Johnson",
          73: "Lafayette",
          75: "Lawrence",
          77: "Lee",
          79: "Lincoln",
          81: "Little River",
          83: "Logan",
          85: "Lonoke",
          87: "Madison",
          89: "Marion",
          91: "Miller",
          93: "Mississippi",
          95: "Monroe",
          97: "Montgomery",
          99: "Nevada",
          101: "Newton",
          103: "Ouachita",
          105: "Perry",
          107: "Phillips",
          109: "Pike",
          111: "Poinsett",
          113: "Polk",
          115: "Pope",
          117: "Prairie",
          119: "Pulaski",
          121: "Randolph",
          123: "St Francis",
          125: "Saline",
          127: "Scott",
          129: "Searcy",
          131: "Sebastian",
          133: "Sevier",
          135: "Sharp",
          137: "Stone",
          139: "Union",
          141: "Van Buren",
          143: "Washington",
          145: "White",
          147: "Woodruff",
          149: "Yell"
      },
      michigan:
      {
          1: "Alcona",
          3: "Alger",
          5: "Allegan",
          7: "Alpena",
          9: "Antrim",
          11: "Arenac",
          13: "Baraga",
          15: "Barry",
          17: "Bay",
          19: "Benzie",
          21: "Berrien",
          23: "Branch",
          25: "Calhoun",
          27: "Cass",
          29: "Charlevoix",
          31: "Cheboygan",
          33: "Chippewa",
          35: "Clare",
          37: "Clinton",
          39: "Crawford",
          41: "Delta",
          43: "Dickinson",
          45: "Eaton",
          47: "Emmet",
          49: "Genesee",
          51: "Gladwin",
          53: "Gogebic",
          55: "Grand Traverse",
          57: "Gratiot",
          59: "Hillsdale",
          61: "Houghton",
          63: "Huron",
          65: "Ingham",
          67: "Ionia",
          69: "Iosco",
          71: "Iron",
          73: "Isabella",
          75: "Jackson",
          77: "Kalamazoo",
          79: "Kalkaska",
          81: "Kent",
          83: "Keweenaw",
          85: "Lake",
          87: "Lapeer",
          89: "Leelanau",
          91: "Lenawee",
          93: "Livingston",
          95: "Luce",
          97: "Mackinac",
          99: "Macomb",
          101: "Manistee",
          103: "Marquette",
          105: "Mason",
          107: "Mecosta",
          109: "Menominee",
          111: "Midland",
          113: "Missaukee",
          115: "Monroe",
          117: "Montcalm",
          119: "Montmorency",
          121: "Muskegon",
          123: "Newaygo",
          125: "Oakland",
          127: "Oceana",
          129: "Ogemaw",
          131: "Ontonagon",
          133: "Osceola",
          135: "Oscoda",
          137: "Otsego",
          139: "Ottawa",
          141: "Presque Isle",
          143: "Roscommon",
          145: "Saginaw",
          147: "St Clair",
          149: "St Joseph",
          151: "Sanilac",
          153: "Schoolcraft",
          155: "Shiawassee",
          157: "Tuscola",
          159: "Van Buren",
          161: "Washtenaw",
          163: "Wayne",
          165: "Wexford"
      },
      americansamoa:
      {
          10: "Unknown",
          20: "Olosega",
          50: "Tutuila"
      },
      northcarolina:
      {
          1: "Alamance",
          3: "Alexander",
          5: "Alleghany",
          7: "Anson",
          9: "Ashe",
          11: "Avery",
          13: "Beaufort",
          15: "Bertie",
          17: "Bladen",
          19: "Brunswick",
          21: "Buncombe",
          23: "Burke",
          25: "Cabarrus",
          27: "Caldwell",
          29: "Camden",
          31: "Carteret",
          33: "Caswell",
          35: "Catawba",
          37: "Chatham",
          39: "Cherokee",
          41: "Chowan",
          43: "Clay",
          45: "Cleveland",
          47: "Columbus",
          49: "Craven",
          51: "Cumberland",
          53: "Currituck",
          55: "Dare",
          57: "Davidson",
          59: "Davie",
          61: "Duplin",
          63: "Durham",
          65: "Edgecombe",
          67: "Forsyth",
          69: "Franklin",
          71: "Gaston",
          73: "Gates",
          75: "Graham",
          77: "Granville",
          79: "Greene",
          81: "Guilford",
          83: "Halifax",
          85: "Harnett",
          87: "Haywood",
          89: "Henderson",
          91: "Hertford",
          93: "Hoke",
          95: "Hyde",
          97: "Iredell",
          99: "Jackson",
          101: "Johnston",
          103: "Jones",
          105: "Lee",
          107: "Lenoir",
          109: "Lincoln",
          111: "McDowell",
          113: "Macon",
          115: "Madison",
          117: "Martin",
          119: "Mecklenburg",
          121: "Mitchell",
          123: "Montgomery",
          125: "Moore",
          127: "Nash",
          129: "New Hanover",
          131: "Northampton",
          133: "Onslow",
          135: "Orange",
          137: "Pamlico",
          139: "Pasquotank",
          141: "Pender",
          143: "Perquimans",
          145: "Person",
          147: "Pitt",
          149: "Polk",
          151: "Randolph",
          153: "Richmond",
          155: "Robeson",
          157: "Rockingham",
          159: "Rowan",
          161: "Rutherford",
          163: "Sampson",
          165: "Scotland",
          167: "Stanly",
          169: "Stokes",
          171: "Surry",
          173: "Swain",
          175: "Transylvania",
          177: "Tyrrell",
          179: "Union",
          181: "Vance",
          183: "Wake",
          185: "Warren",
          187: "Washington",
          189: "Watauga",
          191: "Wayne",
          193: "Wilkes",
          195: "Wilson",
          197: "Yadkin",
          199: "Yancey"
      },
      kentucky:
      {
          1: "Adair",
          3: "Allen",
          5: "Anderson",
          7: "Ballard",
          9: "Barren",
          11: "Bath",
          13: "Bell",
          15: "Boone",
          17: "Bourbon",
          19: "Boyd",
          21: "Boyle",
          23: "Bracken",
          25: "Breathitt",
          27: "Breckinridge",
          29: "Bullitt",
          31: "Butler",
          33: "Caldwell",
          35: "Calloway",
          37: "Campbell",
          39: "Carlisle",
          41: "Carroll",
          43: "Carter",
          45: "Casey",
          47: "Christian",
          49: "Clark",
          51: "Clay",
          53: "Clinton",
          55: "Crittenden",
          57: "Cumberland",
          59: "Daviess",
          61: "Edmonson",
          63: "Elliott",
          65: "Estill",
          67: "Fayette",
          69: "Fleming",
          71: "Floyd",
          73: "Franklin",
          75: "Fulton",
          77: "Gallatin",
          79: "Garrard",
          81: "Grant",
          83: "Graves",
          85: "Grayson",
          87: "Green",
          89: "Greenup",
          91: "Hancock",
          93: "Hardin",
          95: "Harlan",
          97: "Harrison",
          99: "Hart",
          101: "Henderson",
          103: "Henry",
          105: "Hickman",
          107: "Hopkins",
          109: "Jackson",
          111: "Jefferson",
          113: "Jessamine",
          115: "Johnson",
          117: "Kenton",
          119: "Knott",
          121: "Knox",
          123: "Larue",
          125: "Laurel",
          127: "Lawrence",
          129: "Lee",
          131: "Leslie",
          133: "Letcher",
          135: "Lewis",
          137: "Lincoln",
          139: "Livingston",
          141: "Logan",
          143: "Lyon",
          145: "McCracken",
          147: "McCreary",
          149: "McLean",
          151: "Madison",
          153: "Magoffin",
          155: "Marion",
          157: "Marshall",
          159: "Martin",
          161: "Mason",
          163: "Meade",
          165: "Menifee",
          167: "Mercer",
          169: "Metcalfe",
          171: "Monroe",
          173: "Montgomery",
          175: "Morgan",
          177: "Muhlenberg",
          179: "Nelson",
          181: "Nicholas",
          183: "Ohio",
          185: "Oldham",
          187: "Owen",
          189: "Owsley",
          191: "Pendleton",
          193: "Perry",
          195: "Pike",
          197: "Powell",
          199: "Pulaski",
          201: "Robertson",
          203: "Rockcastle",
          205: "Rowan",
          207: "Russell",
          209: "Scott",
          211: "Shelby",
          213: "Simpson",
          215: "Spencer",
          217: "Taylor",
          219: "Todd",
          221: "Trigg",
          223: "Trimble",
          225: "Union",
          227: "Warren",
          229: "Washington",
          231: "Wayne",
          233: "Webster",
          235: "Whitley",
          237: "Wolfe",
          239: "Woodford"
      },
      northernmarianaislands:
      {
          10: "Cocos Island",
          100: "Rota",
          110: "Saipan",
          120: "Tinian"
      },
      tennessee:
      {
          1: "Anderson",
          3: "Bedford",
          5: "Benton",
          7: "Bledsoe",
          9: "Blount",
          11: "Bradley",
          13: "Campbell",
          15: "Cannon",
          17: "Carroll",
          19: "Carter",
          21: "Cheatham",
          23: "Chester",
          25: "Claiborne",
          27: "Clay",
          29: "Cocke",
          31: "Coffee",
          33: "Crockett",
          35: "Cumberland",
          37: "Davidson",
          39: "Decatur",
          41: "De Kalb",
          43: "Dickson",
          45: "Dyer",
          47: "Fayette",
          49: "Fentress",
          51: "Franklin",
          53: "Gibson",
          55: "Giles",
          57: "Grainger",
          59: "Greene",
          61: "Grundy",
          63: "Hamblen",
          65: "Hamilton",
          67: "Hancock",
          69: "Hardeman",
          71: "Hardin",
          73: "Hawkins",
          75: "Haywood",
          77: "Henderson",
          79: "Henry",
          81: "Hickman",
          83: "Houston",
          85: "Humphreys",
          87: "Jackson",
          89: "Jefferson",
          91: "Johnson",
          93: "Knox",
          95: "Lake",
          97: "Lauderdale",
          99: "Lawrence",
          101: "Lewis",
          103: "Lincoln",
          105: "Loudon",
          107: "McMinn",
          109: "McNairy",
          111: "Macon",
          113: "Madison",
          115: "Marion",
          117: "Marshall",
          119: "Maury",
          121: "Meigs",
          123: "Monroe",
          125: "Montgomery",
          127: "Moore",
          129: "Morgan",
          131: "Obion",
          133: "Overton",
          135: "Perry",
          137: "Pickett",
          139: "Polk",
          141: "Putnam",
          143: "Rhea",
          145: "Roane",
          147: "Robertson",
          149: "Rutherford",
          151: "Scott",
          153: "Sequatchie",
          155: "Sevier",
          157: "Shelby",
          159: "Smith",
          161: "Stewart",
          163: "Sullivan",
          165: "Sumner",
          167: "Tipton",
          169: "Trousdale",
          171: "Unicoi",
          173: "Union",
          175: "Van Buren",
          177: "Warren",
          179: "Washington",
          181: "Wayne",
          183: "Weakley",
          185: "White",
          187: "Williamson",
          189: "Wilson"
      },
      newjersey:
      {
          1: "Atlantic",
          3: "Bergen",
          5: "Burlington",
          7: "Camden",
          9: "Cape May",
          11: "Cumberland",
          13: "Essex",
          15: "Gloucester",
          17: "Hudson",
          19: "Hunterdon",
          21: "Mercer",
          23: "Middlesex",
          25: "Monmouth",
          27: "Morris",
          29: "Ocean",
          31: "Passaic",
          33: "Salem",
          35: "Somerset",
          37: "Sussex",
          39: "Union",
          41: "Warren"
      },
      puertorico:
      {
          1: "Adjuntas",
          3: "Aguada",
          5: "Aguadilla",
          7: "Aguas Buenas",
          9: "Aibonito",
          11: "Anasco",
          13: "Arecibo",
          15: "Arroyo",
          17: "Barceloneta",
          19: "Barranquitas",
          21: "Bayamon",
          23: "Cabo Rojo",
          25: "Caguas",
          27: "Camuy",
          29: "Canovanas",
          31: "Carolina",
          33: "Catano",
          35: "Cayey",
          37: "Ceiba",
          41: "Cidra",
          43: "Coamo",
          45: "Comerio",
          47: "Corozal",
          49: "Culebra",
          51: "Dorado",
          53: "Fajardo",
          54: "Florida",
          55: "Guanica",
          57: "Guayama",
          59: "Guayanilla",
          61: "Guaynabo",
          63: "Gurabo",
          65: "Hatillo",
          67: "Hormigueros",
          71: "Isabela",
          73: "Jayuya",
          75: "Juana Diaz",
          77: "Juncos",
          79: "Lajas",
          81: "Lares",
          83: "Las Marias",
          85: "Las Piedras",
          87: "Loiza",
          89: "Luquillo",
          91: "Manati",
          93: "Maricao",
          95: "Maunabo",
          97: "Mayaguez",
          99: "Moca",
          101: "Morovis",
          103: "Nabuabo",
          105: "Naranjito",
          107: "Orocovis",
          109: "Patillas",
          111: "Penuelas",
          113: "Ponce",
          115: "Quebradillas",
          117: "Rincon",
          119: "Rio Grande",
          121: "Sabana Grande",
          123: "Salinas",
          125: "San German",
          127: "San Juan",
          129: "San Lorenzo",
          131: "San Sabastian",
          133: "Santa Isabel",
          135: "Toa Alta",
          137: "Toa Baja",
          139: "Trujillo Alto",
          141: "Utuado",
          143: "Vega Alta",
          145: "Vega Baja",
          147: "Vieques",
          149: "Villalba",
          151: "Yabucoa",
          153: "Yauco"
      },
      montana:
      {
          1: "Beaverhead",
          3: "Big Horn",
          5: "Blaine",
          7: "Broadwater",
          9: "Carbon",
          11: "Carter",
          13: "Cascade",
          15: "Chouteau",
          17: "Custer",
          19: "Daniels",
          21: "Dawson",
          23: "Deer Lodge",
          25: "Fallon",
          27: "Fergus",
          29: "Flathead",
          31: "Gallatin",
          33: "Garfield",
          35: "Glacier",
          37: "Golden Valley",
          39: "Granite",
          41: "Hill",
          43: "Jefferson",
          45: "Judith Basin",
          47: "Lake",
          49: "Lewis and Clark",
          51: "Liberty",
          53: "Lincoln",
          55: "McCone",
          57: "Madison",
          59: "Meagher",
          61: "Mineral",
          63: "Missoula",
          65: "Musselshell",
          67: "Park",
          69: "Petroleum",
          71: "Phillips",
          73: "Pondera",
          75: "Powder River",
          77: "Powell",
          79: "Prairie",
          81: "Ravalli",
          83: "Richland",
          85: "Roosevelt",
          87: "Rosebud",
          89: "Sanders",
          91: "Sheridan",
          93: "Silver Bow",
          95: "Stillwater",
          97: "Sweet Grass",
          99: "Teton",
          101: "Toole",
          103: "Treasure",
          105: "Valley",
          107: "Wheatland",
          109: "Wibaux",
          111: "Yellowstone",
          113: "Yellowstone Nat Park"
      },
      utah:
      {
          1: "Beaver",
          3: "Box Elder",
          5: "Cache",
          7: "Carbon",
          9: "Daggett",
          11: "Davis",
          13: "Duchesne",
          15: "Emery",
          17: "Garfield",
          19: "Grand",
          21: "Iron",
          23: "Juab",
          25: "Kane",
          27: "Millard",
          29: "Morgan",
          31: "Piute",
          33: "Rich",
          35: "Salt Lake",
          37: "San Juan",
          39: "Sanpete",
          41: "Sevier",
          43: "Summit",
          45: "Tooele",
          47: "Uintah",
          49: "Utah",
          51: "Wasatch",
          53: "Washington",
          55: "Wayne",
          57: "Weber"
      },
      florida:
      {
          1: "Alachua",
          3: "Baker",
          5: "Bay",
          7: "Bradford",
          9: "Brevard",
          11: "Broward",
          13: "Calhoun",
          15: "Charlotte",
          17: "Citrus",
          19: "Clay",
          21: "Collier",
          23: "Columbia",
          27: "De Soto",
          29: "Dixie",
          31: "Duval",
          33: "Escambia",
          35: "Flagler",
          37: "Franklin",
          39: "Gadsden",
          41: "Gilchrist",
          43: "Glades",
          45: "Gulf",
          47: "Hamilton",
          49: "Hardee",
          51: "Hendry",
          53: "Hernando",
          55: "Highlands",
          57: "Hillsborough",
          59: "Holmes",
          61: "Indian River",
          63: "Jackson",
          65: "Jefferson",
          67: "Lafayette",
          69: "Lake",
          71: "Lee",
          73: "Leon",
          75: "Levy",
          77: "Liberty",
          79: "Madison",
          81: "Manatee",
          83: "Marion",
          85: "Martin",
          86: "Miami-Dade",
          87: "Monroe",
          89: "Nassau",
          91: "Okaloosa",
          93: "Okeechobee",
          95: "Orange",
          97: "Osceola",
          99: "Palm Beach",
          101: "Pasco",
          103: "Pinellas",
          105: "Polk",
          107: "Putnam",
          109: "St Johns",
          111: "St Lucie",
          113: "Santa Rosa",
          115: "Sarasota",
          117: "Seminole",
          119: "Sumter",
          121: "Suwannee",
          123: "Taylor",
          125: "Union",
          127: "Volusia",
          129: "Wakulla",
          131: "Walton",
          133: "Washington"
      },
      colorado:
      {
          1: "Adams",
          3: "Alamosa",
          5: "Arapahoe",
          7: "Archuleta",
          9: "Baca",
          11: "Bent",
          13: "Boulder",
          15: "Chaffee",
          17: "Cheyenne",
          19: "Clear Creek",
          21: "Conejos",
          23: "Costilla",
          25: "Crowley",
          27: "Custer",
          29: "Delta",
          31: "Denver",
          33: "Dolores",
          35: "Douglas",
          37: "Eagle",
          39: "Elbert",
          41: "El Paso",
          43: "Fremont",
          45: "Garfield",
          47: "Gilpin",
          49: "Grand",
          51: "Gunnison",
          53: "Hinsdale",
          55: "Huerfano",
          57: "Jackson",
          59: "Jefferson",
          61: "Kiowa",
          63: "Kit Carson",
          65: "Lake",
          67: "La Plata",
          69: "Larimer",
          71: "Las Animas",
          73: "Lincoln",
          75: "Logan",
          77: "Mesa",
          79: "Mineral",
          81: "Moffat",
          83: "Montezuma",
          85: "Montrose",
          87: "Morgan",
          89: "Otero",
          91: "Ouray",
          93: "Park",
          95: "Phillips",
          97: "Pitkin",
          99: "Prowers",
          101: "Pueblo",
          103: "Rio Blanco",
          105: "Rio Grande",
          107: "Routt",
          109: "Saguache",
          111: "San Juan",
          113: "San Miguel",
          115: "Sedgwick",
          117: "Summit",
          119: "Teller",
          121: "Washington",
          123: "Weld",
          125: "Yuma"
      },
      illinois:
      {
          1: "Adams",
          3: "Alexander",
          5: "Bond",
          7: "Boone",
          9: "Brown",
          11: "Bureau",
          13: "Calhoun",
          15: "Carroll",
          17: "Cass",
          19: "Champaign",
          21: "Christian",
          23: "Clark",
          25: "Clay",
          27: "Clinton",
          29: "Coles",
          31: "Cook",
          33: "Crawford",
          35: "Cumberland",
          37: "De Kalb",
          39: "De Witt",
          41: "Douglas",
          43: "Du Page",
          45: "Edgar",
          47: "Edwards",
          49: "Effingham",
          51: "Fayette",
          53: "Ford",
          55: "Franklin",
          57: "Fulton",
          59: "Gallatin",
          61: "Greene",
          63: "Grundy",
          65: "Hamilton",
          67: "Hancock",
          69: "Hardin",
          71: "Henderson",
          73: "Henry",
          75: "Iroquois",
          77: "Jackson",
          79: "Jasper",
          81: "Jefferson",
          83: "Jersey",
          85: "Jo Daviess",
          87: "Johnson",
          89: "Kane",
          91: "Kankakee",
          93: "Kendall",
          95: "Knox",
          97: "Lake",
          99: "La Salle",
          101: "Lawrence",
          103: "Lee",
          105: "Livingston",
          107: "Logan",
          109: "McDonough",
          111: "McHenry",
          113: "McLean",
          115: "Macon",
          117: "Macoupin",
          119: "Madison",
          121: "Marion",
          123: "Marshall",
          125: "Mason",
          127: "Massac",
          129: "Menard",
          131: "Mercer",
          133: "Monroe",
          135: "Montgomery",
          137: "Morgan",
          139: "Moultrie",
          141: "Ogle",
          143: "Peoria",
          145: "Perry",
          147: "Piatt",
          149: "Pike",
          151: "Pope",
          153: "Pulaski",
          155: "Putnam",
          157: "Randolph",
          159: "Richland",
          161: "Rock Island",
          163: "St Clair",
          165: "Saline",
          167: "Sangamon",
          169: "Schuyler",
          171: "Scott",
          173: "Shelby",
          175: "Stark",
          177: "Stephenson",
          179: "Tazewell",
          181: "Union",
          183: "Vermilion",
          185: "Wabash",
          187: "Warren",
          189: "Washington",
          191: "Wayne",
          193: "White",
          195: "Whiteside",
          197: "Will",
          199: "Williamson",
          201: "Winnebago",
          203: "Woodford"
      },
      indiana:
      {
          1: "Adams",
          3: "Allen",
          5: "Bartholomew",
          7: "Benton",
          9: "Blackford",
          11: "Boone",
          13: "Brown",
          15: "Carroll",
          17: "Cass",
          19: "Clark",
          21: "Clay",
          23: "Clinton",
          25: "Crawford",
          27: "Daviess",
          29: "Dearborn",
          31: "Decatur",
          33: "De Kalb",
          35: "Delaware",
          37: "Dubois",
          39: "Elkhart",
          41: "Fayette",
          43: "Floyd",
          45: "Fountain",
          47: "Franklin",
          49: "Fulton",
          51: "Gibson",
          53: "Grant",
          55: "Greene",
          57: "Hamilton",
          59: "Hancock",
          61: "Harrison",
          63: "Hendricks",
          65: "Henry",
          67: "Howard",
          69: "Huntington",
          71: "Jackson",
          73: "Jasper",
          75: "Jay",
          77: "Jefferson",
          79: "Jennings",
          81: "Johnson",
          83: "Knox",
          85: "Kosciusko",
          87: "La Grange",
          89: "Lake",
          91: "La Porte",
          93: "Lawrence",
          95: "Madison",
          97: "Marion",
          99: "Marshall",
          101: "Martin",
          103: "Miami",
          105: "Monroe",
          107: "Montgomery",
          109: "Morgan",
          111: "Newton",
          113: "Noble",
          115: "Ohio",
          117: "Orange",
          119: "Owen",
          121: "Parke",
          123: "Perry",
          125: "Pike",
          127: "Porter",
          129: "Posey",
          131: "Pulaski",
          133: "Putnam",
          135: "Randolph",
          137: "Ripley",
          139: "Rush",
          141: "St Joseph",
          143: "Scott",
          145: "Shelby",
          147: "Spencer",
          149: "Starke",
          151: "Steuben",
          153: "Sullivan",
          155: "Switzerland",
          157: "Tippecanoe",
          159: "Tipton",
          161: "Union",
          163: "Vanderburgh",
          165: "Vermillion",
          167: "Vigo",
          169: "Wabash",
          171: "Warren",
          173: "Warrick",
          175: "Washington",
          177: "Wayne",
          179: "Wells",
          181: "White",
          183: "Whitley"
      },
      minnesota:
      {
          1: "Aitkin",
          3: "Anoka",
          5: "Becker",
          7: "Beltrami",
          9: "Benton",
          11: "Big Stone",
          13: "Blue Earth",
          15: "Brown",
          17: "Carlton",
          19: "Carver",
          21: "Cass",
          23: "Chippewa",
          25: "Chisago",
          27: "Clay",
          29: "Clearwater",
          31: "Cook",
          33: "Cottonwood",
          35: "Crow Wing",
          37: "Dakota",
          39: "Dodge",
          41: "Douglas",
          43: "Faribault",
          45: "Fillmore",
          47: "Freeborn",
          49: "Goodhue",
          51: "Grant",
          53: "Hennepin",
          55: "Houston",
          57: "Hubbard",
          59: "Isanti",
          61: "Itasca",
          63: "Jackson",
          65: "Kanabec",
          67: "Kandiyohi",
          69: "Kittson",
          71: "Koochiching",
          73: "Lac qui Parle",
          75: "Lake",
          77: "Lake of the Woods",
          79: "Le Sueur",
          81: "Lincoln",
          83: "Lyon",
          85: "McLeod",
          87: "Mahnomen",
          89: "Marshall",
          91: "Martin",
          93: "Meeker",
          95: "Mille Lacs",
          97: "Morrison",
          99: "Mower",
          101: "Murray",
          103: "Nicollet",
          105: "Nobles",
          107: "Norman",
          109: "Olmsted",
          111: "Otter Tail",
          113: "Pennington",
          115: "Pine",
          117: "Pipestone",
          119: "Polk",
          121: "Pope",
          123: "Ramsey",
          125: "Red Lake",
          127: "Redwood",
          129: "Renville",
          131: "Rice",
          133: "Rock",
          135: "Roseau",
          137: "St Louis",
          139: "Scott",
          141: "Sherburne",
          143: "Sibley",
          145: "Stearns",
          147: "Steele",
          149: "Stevens",
          151: "Swift",
          153: "Todd",
          155: "Traverse",
          157: "Wabasha",
          159: "Wadena",
          161: "Waseca",
          163: "Washington",
          165: "Watonwan",
          167: "Wilkin",
          169: "Winona",
          171: "Wright",
          173: "Yellow Medicine"
      },
      oklahoma:
      {
          1: "Adair",
          3: "Alfalfa",
          5: "Atoka",
          7: "Beaver",
          9: "Beckham",
          11: "Blaine",
          13: "Bryan",
          15: "Caddo",
          17: "Canadian",
          19: "Carter",
          21: "Cherokee",
          23: "Choctaw",
          25: "Cimarron",
          27: "Cleveland",
          29: "Coal",
          31: "Comanche",
          33: "Cotton",
          35: "Craig",
          37: "Creek",
          39: "Custer",
          41: "Delaware",
          43: "Dewey",
          45: "Ellis",
          47: "Garfield",
          49: "Garvin",
          51: "Grady",
          53: "Grant",
          55: "Greer",
          57: "Harmon",
          59: "Harper",
          61: "Haskell",
          63: "Hughes",
          65: "Jackson",
          67: "Jefferson",
          69: "Johnston",
          71: "Kay",
          73: "Kingfisher",
          75: "Kiowa",
          77: "Latimer",
          79: "Le Flore",
          81: "Lincoln",
          83: "Logan",
          85: "Love",
          87: "McClain",
          89: "McCurtain",
          91: "McIntosh",
          93: "Major",
          95: "Marshall",
          97: "Mayes",
          99: "Murray",
          101: "Muskogee",
          103: "Noble",
          105: "Nowata",
          107: "Okfuskee",
          109: "Oklahoma",
          111: "Okmulgee",
          113: "Osage",
          115: "Ottawa",
          117: "Pawnee",
          119: "Payne",
          121: "Pittsburg",
          123: "Pontotoc",
          125: "Pottawatomie",
          127: "Pushmataha",
          129: "Roger Mills",
          131: "Rogers",
          133: "Seminole",
          135: "Sequoyah",
          137: "Stephens",
          139: "Texas",
          141: "Tillman",
          143: "Tulsa",
          145: "Wagoner",
          147: "Washington",
          149: "Washita",
          151: "Woods",
          153: "Woodward"
      },
      guam:
      {
          10: "Guam"
      },
      connecticut:
      {
          1: "Fairfield",
          3: "Hartford",
          5: "Litchfield",
          7: "Middlesex",
          9: "New Haven",
          11: "New London",
          13: "Tolland",
          15: "Windham"
      },
      iowa:
      {
          1: "Adair",
          3: "Adams",
          5: "Allamakee",
          7: "Appanoose",
          9: "Audubon",
          11: "Benton",
          13: "Black Hawk",
          15: "Boone",
          17: "Bremer",
          19: "Buchanan",
          21: "Buena Vista",
          23: "Butler",
          25: "Calhoun",
          27: "Carroll",
          29: "Cass",
          31: "Cedar",
          33: "Cerro Gordo",
          35: "Cherokee",
          37: "Chickasaw",
          39: "Clarke",
          41: "Clay",
          43: "Clayton",
          45: "Clinton",
          47: "Crawford",
          49: "Dallas",
          51: "Davis",
          53: "Decatur",
          55: "Delaware",
          57: "Des Moines",
          59: "Dickinson",
          61: "Dubuque",
          63: "Emmet",
          65: "Fayette",
          67: "Floyd",
          69: "Franklin",
          71: "Fremont",
          73: "Greene",
          75: "Grundy",
          77: "Guthrie",
          79: "Hamilton",
          81: "Hancock",
          83: "Hardin",
          85: "Harrison",
          87: "Henry",
          89: "Howard",
          91: "Humboldt",
          93: "Ida",
          95: "Iowa",
          97: "Jackson",
          99: "Jasper",
          101: "Jefferson",
          103: "Johnson",
          105: "Jones",
          107: "Keokuk",
          109: "Kossuth",
          111: "Lee",
          113: "Linn",
          115: "Louisa",
          117: "Lucas",
          119: "Lyon",
          121: "Madison",
          123: "Mahaska",
          125: "Marion",
          127: "Marshall",
          129: "Mills",
          131: "Mitchell",
          133: "Monona",
          135: "Monroe",
          137: "Montgomery",
          139: "Muscatine",
          141: "O Brien",
          143: "Osceola",
          145: "Page",
          147: "Palo Alto",
          149: "Plymouth",
          151: "Pocahontas",
          153: "Polk",
          155: "Pottawattamie",
          157: "Poweshiek",
          159: "Ringgold",
          161: "Sac",
          163: "Scott",
          165: "Shelby",
          167: "Sioux",
          169: "Story",
          171: "Tama",
          173: "Taylor",
          175: "Union",
          177: "Van Buren",
          179: "Wapello",
          181: "Warren",
          183: "Washington",
          185: "Wayne",
          187: "Webster",
          189: "Winnebago",
          191: "Winneshiek",
          193: "Woodbury",
          195: "Worth",
          197: "Wright"
      },
      maine:
      {
          1: "Androscoggin",
          3: "Aroostook",
          5: "Cumberland",
          7: "Franklin",
          9: "Hancock",
          11: "Kennebec",
          13: "Knox",
          15: "Lincoln",
          17: "Oxford",
          19: "Penobscot",
          21: "Piscataquis",
          23: "Sagadahoc",
          25: "Somerset",
          27: "Waldo",
          29: "Washington",
          31: "York"
      },
      arizona:
      {
          1: "Apache",
          3: "Cochise",
          5: "Coconino",
          7: "Gila",
          9: "Graham",
          11: "Greenlee",
          12: "La Paz",
          13: "Maricopa",
          15: "Mohave",
          17: "Navajo",
          19: "Pima",
          21: "Pinal",
          23: "Santa Cruz",
          25: "Yavapai",
          27: "Yuma"
      },
      missouri:
      {
          1: "Adair",
          3: "Andrew",
          5: "Atchison",
          7: "Audrain",
          9: "Barry",
          11: "Barton",
          13: "Bates",
          15: "Benton",
          17: "Bollinger",
          19: "Boone",
          21: "Buchanan",
          23: "Butler",
          25: "Caldwell",
          27: "Callaway",
          29: "Camden",
          31: "Cape Girardeau",
          33: "Carroll",
          35: "Carter",
          37: "Cass",
          39: "Cedar",
          41: "Chariton",
          43: "Christian",
          45: "Clark",
          47: "Clay",
          49: "Clinton",
          51: "Cole",
          53: "Cooper",
          55: "Crawford",
          57: "Dade",
          59: "Dallas",
          61: "Daviess",
          63: "De Kalb",
          65: "Dent",
          67: "Douglas",
          69: "Dunklin",
          71: "Franklin",
          73: "Gasconade",
          75: "Gentry",
          77: "Greene",
          79: "Grundy",
          81: "Harrison",
          83: "Henry",
          85: "Hickory",
          87: "Holt",
          89: "Howard",
          91: "Howell",
          93: "Iron",
          95: "Jackson",
          97: "Jasper",
          99: "Jefferson",
          101: "Johnson",
          103: "Knox",
          105: "Laclede",
          107: "Lafayette",
          109: "Lawrence",
          111: "Lewis",
          113: "Lincoln",
          115: "Linn",
          117: "Livingston",
          119: "McDonald",
          121: "Macon",
          123: "Madison",
          125: "Maries",
          127: "Marion",
          129: "Mercer",
          131: "Miller",
          133: "Mississippi",
          135: "Moniteau",
          137: "Monroe",
          139: "Montgomery",
          141: "Morgan",
          143: "New Madrid",
          145: "Newton",
          147: "Nodaway",
          149: "Oregon",
          151: "Osage",
          153: "Ozark",
          155: "Pemiscot",
          157: "Perry",
          159: "Pettis",
          161: "Phelps",
          163: "Pike",
          165: "Platte",
          167: "Polk",
          169: "Pulaski",
          171: "Putnam",
          173: "Ralls",
          175: "Randolph",
          177: "Ray",
          179: "Reynolds",
          181: "Ripley",
          183: "St Charles",
          185: "St Clair",
          186: "Ste. Genevieve",
          187: "St Francois",
          189: "St Louis",
          195: "Saline",
          197: "Schuyler",
          199: "Scotland",
          201: "Scott",
          203: "Shannon",
          205: "Shelby",
          207: "Stoddard",
          209: "Stone",
          211: "Sullivan",
          213: "Taney",
          215: "Texas",
          217: "Vernon",
          219: "Warren",
          221: "Washington",
          223: "Wayne",
          225: "Webster",
          227: "Worth",
          229: "Wright",
          510: "St Louis City"
      },
      newhampshire:
      {
          1: "Belknap",
          3: "Carroll",
          5: "Cheshire",
          7: "Coos",
          9: "Grafton",
          11: "Hillsborough",
          13: "Merrimack",
          15: "Rockingham",
          17: "Strafford",
          19: "Sullivan"
      },
      california:
      {
          1: "Alameda",
          3: "Alpine",
          5: "Amador",
          7: "Butte",
          9: "Calaveras",
          11: "Colusa",
          13: "Contra Costa",
          15: "Del Norte",
          17: "El Dorado",
          19: "Fresno",
          21: "Glenn",
          23: "Humboldt",
          25: "Imperial",
          27: "Inyo",
          29: "Kern",
          31: "Kings",
          33: "Lake",
          35: "Lassen",
          37: "Los Angeles",
          39: "Madera",
          41: "Marin",
          43: "Mariposa",
          45: "Mendocino",
          47: "Merced",
          49: "Modoc",
          51: "Mono",
          53: "Monterey",
          55: "Napa",
          57: "Nevada",
          59: "Orange",
          61: "Placer",
          63: "Plumas",
          65: "Riverside",
          67: "Sacramento",
          69: "San Benito",
          71: "San Bernardino",
          73: "San Diego",
          75: "San Francisco",
          77: "San Joaquin",
          79: "San Luis Obispo",
          81: "San Mateo",
          83: "Santa Barbara",
          85: "Santa Clara",
          87: "Santa Cruz",
          89: "Shasta",
          91: "Sierra",
          93: "Siskiyou",
          95: "Solano",
          97: "Sonoma",
          99: "Stanislaus",
          101: "Sutter",
          103: "Tehama",
          105: "Trinity",
          107: "Tulare",
          109: "Tuolumne",
          111: "Ventura",
          113: "Yolo",
          115: "Yuba"
      },
      maryland:
      {
          1: "Allegany",
          3: "Anne Arundel",
          5: "Baltimore",
          9: "Calvert",
          11: "Caroline",
          13: "Carroll",
          15: "Cecil",
          17: "Charles",
          19: "Dorchester",
          21: "Frederick",
          23: "Garrett",
          25: "Harford",
          27: "Howard",
          29: "Kent",
          31: "Montgomery",
          33: "Prince Georges",
          35: "Queen Annes",
          37: "St Marys",
          39: "Somerset",
          41: "Talbot",
          43: "Washington",
          45: "Wicomico",
          47: "Worcester",
          510: "Baltimore City"
      },
      rhodeisland:
      {
          1: "Bristol",
          3: "Kent",
          5: "Newport",
          7: "Providence",
          9: "Washington"
      },
      idaho:
      {
          1: "Ada",
          3: "Adams",
          5: "Bannock",
          7: "Bear Lake",
          9: "Benewah",
          11: "Bingham",
          13: "Blaine",
          15: "Boise",
          17: "Bonner",
          19: "Bonneville",
          21: "Boundary",
          23: "Butte",
          25: "Camas",
          27: "Canyon",
          29: "Caribou",
          31: "Cassia",
          33: "Clark",
          35: "Clearwater",
          37: "Custer",
          39: "Elmore",
          41: "Franklin",
          43: "Fremont",
          45: "Gem",
          47: "Gooding",
          49: "Idaho",
          51: "Jefferson",
          53: "Jerome",
          55: "Kootenai",
          57: "Latah",
          59: "Lemhi",
          61: "Lewis",
          63: "Lincoln",
          65: "Madison",
          67: "Minidoka",
          69: "Nez Perce",
          71: "Oneida",
          73: "Owyhee",
          75: "Payette",
          77: "Power",
          79: "Shoshone",
          81: "Teton",
          83: "Twin Falls",
          85: "Valley",
          87: "Washington"
      }
  };
  let st = state.toLowerCase().replace(' ', '');
  return codes[st][parseInt(fips)]
}
